import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { io } from "socket.io-client";
import { Archive, ArchiveRestore, Paperclip, Send, MessageSquarePlus, X, Settings, PlusCircle, MessageSquare as MessageSquareIcon, Trash, Pencil, LayoutDashboard, Copy, LogOut, Eye, EyeOff, Users as UsersIcon, Calculator, TrendingUp, StickyNote, Save, Info, Star, Briefcase, Calendar, Tag, CheckCheck, Link as LinkIcon, Smile, ChevronDown, ArrowLeft, MoreVertical, Search, RefreshCw, Clock, Play, Pause, UploadCloud, ArrowRight, Smartphone, UserCircle, MessageSquareText, ThumbsUp, Bell, ThumbsDown, CornerDownLeft } from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { db, auth } from './firebase';
import { collection, onSnapshot, query, where, getDocs, doc, addDoc, updateDoc, deleteDoc, orderBy, arrayUnion, arrayRemove, serverTimestamp, setDoc, limit, getDoc, collectionGroup, limitToLast } from 'firebase/firestore';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import hoprinPlusLogo from './assets/logo.png';
import loginImage from './assets/login-image.jpg';

// --- Lógica de Notificaciones ---
const notificationSound = new Audio('/notification.mp3');
const requestNotificationPermission = async () => { if (!('Notification' in window)) return; if (Notification.permission !== 'granted') await Notification.requestPermission(); };
const showNotification = (title, options) => {
    if (Notification.permission === 'granted') {
        const notification = new Notification(title, options); // Guardamos la instancia
        notificationSound.play().catch(e => console.error("Error al reproducir sonido:", e));
        
        // Opcional: Cerrar la notificación después de unos segundos
        setTimeout(() => notification.close(), 5000); 
    }
};

// --- CONSTANTES Y HELPERS ---
const TAG_COLORS = { red: 'bg-red-500', blue: 'bg-blue-500', green: 'bg-green-500', yellow: 'bg-yellow-500', purple: 'bg-purple-500', pink: 'bg-pink-500', cyan: 'bg-cyan-500', orange: 'bg-orange-500', lime: 'bg-lime-500', gray: 'bg-gray-500' };
const formatTimestamp = (timestamp) => { if (!timestamp) return ''; const date = timestamp.toDate(); return date.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' }); };

const formatDuration = (ms) => {
    if (isNaN(ms) || ms <= 0) return '0m 0s';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    let result = '';
    if (hours > 0) result += `${hours}h `;
    result += `${minutes}m ${seconds}s`;
    return result.trim();
};

const isSameDay = (d1, d2) => {
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
};
const formatDateSeparator = (date) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (isSameDay(date, today)) return 'HOY';
    if (isSameDay(date, yesterday)) return 'AYER';
    return date.toLocaleDateString('es-VE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

// ==================================================================
// --- INICIO: BLOQUE DE COMPONENTES AUXILIARES ---
// ==================================================================

const MessageStatus = ({ status }) => {
    if (status === 'read') return <CheckCheck size={16} className="text-blue-400" />;
    if (status === 'delivered') return <CheckCheck size={16} className="text-gray-500" />;
    if (status === 'sent') return <CheckCheck size={16} className="text-gray-500" />;
    return null;
};

const Modal = ({ children, onClose, size = 'lg' }) => ( <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 animate-fade-in p-4"> <div className={`bg-gray-800 rounded-lg shadow-2xl p-6 md:p-8 max-w-${size} w-full relative transform transition-all animate-scale-in text-white`}> <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition-colors"><X size={24} /></button> {children} </div> </div> );
const ConfirmationModal = ({ onConfirm, onCancel, title, message }) => ( <Modal onClose={onCancel} size="md"> <h2 className="text-2xl font-bold text-center mb-4">{title}</h2> <p className="text-center text-gray-300 mb-8">{message}</p> <div className="flex justify-center gap-4"> <button onClick={onCancel} className="px-6 py-2 rounded-lg bg-gray-600 hover:bg-gray-500 transition-colors">Cancelar</button> <button onClick={onConfirm} className="px-6 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors">Confirmar</button> </div> </Modal> );

const Avatar = React.memo(({ internalId, size = 'md' }) => {
    const COLORS = { '1': 'bg-blue-500', '2': 'bg-green-500', '3': 'bg-purple-500', 'default': 'bg-gray-500' };
    const firstDigit = internalId ? internalId.toString()[0] : 'default';
    const bgColor = COLORS[firstDigit] || COLORS['default'];
    const sizeClasses = { sm: 'w-10 h-10', md: 'w-12 h-12', lg: 'w-24 h-24' };
    const iconSize = { sm: 20, md: 24, lg: 48 };
    return ( <div className={`rounded-full flex items-center justify-center font-bold text-white shrink-0 ${sizeClasses[size]} ${bgColor}`}> <UsersIcon size={iconSize[size]} /> </div> );
});

const AudioPlayer = ({ src }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);

    const togglePlayPause = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play();
        }
        setIsPlaying(!isPlaying);
    };

    const formatTime = (time) => {
        if (isNaN(time) || time === 0) return '00:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        
        const setAudioData = () => {
            setDuration(audio.duration);
            setCurrentTime(audio.currentTime);
        };
        const setAudioTime = () => setCurrentTime(audio.currentTime);
        const handleEnd = () => setIsPlaying(false);

        audio.addEventListener('loadedmetadata', setAudioData);
        audio.addEventListener('timeupdate', setAudioTime);
        audio.addEventListener('ended', handleEnd);

        return () => {
            audio.removeEventListener('loadedmetadata', setAudioData);
            audio.removeEventListener('timeupdate', setAudioTime);
            audio.removeEventListener('ended', handleEnd);
        };
    }, []);

    const progress = duration ? (currentTime / duration) * 100 : 0;

    return (
        <div className="flex items-center gap-2 w-64">
             <audio ref={audioRef} src={src} preload="metadata"></audio>
            <button onClick={togglePlayPause} className="p-1.5 bg-gray-700/50 rounded-full text-white hover:bg-gray-600">
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </button>
            <div className="flex-grow bg-gray-600 rounded-full h-1.5">
                <div style={{ width: `${progress}%` }} className="bg-hoprin-accent h-1.5 rounded-full"></div>
            </div>
            <span className="text-xs font-mono text-gray-400">{formatTime(duration)}</span>
        </div>
    );
};


const QuotedMessageDisplay = ({ quotedMessage, onQuoteClick }) => { // <-- Añadido onQuoteClick
    if (!quotedMessage) return null;
    
    // Comprobar si el mensaje citado fue enviado por el agente
    const isAgentQuote = quotedMessage.sender === 'agent';
    
    // Comprobar si tenemos un ID para hacerlo 'clickable'
    const canScroll = onQuoteClick && quotedMessage.id;

    return (
        <div 
            onClick={() => canScroll && onQuoteClick(quotedMessage.id)} // <-- Acción de Clic
            className={`p-2 rounded-lg mb-2 ${isAgentQuote ? 'bg-black/20' : 'bg-gray-700/80'} border-l-4 ${isAgentQuote ? 'border-hoprin-accent' : 'border-blue-400'} ${canScroll ? 'cursor-pointer hover:bg-gray-700/50' : ''}`} // <-- Estilos de Clic
        >
            <p className={`text-xs font-bold ${isAgentQuote ? 'text-hoprin-accent' : 'text-blue-400'}`}>
                {/* Mostramos a quién está respondiendo el cliente */}
                {isAgentQuote ? 'Respondiendo a ti' : 'Respondiendo al Cliente'}
            </p>
            <p className="text-sm text-white/80 truncate">
                {quotedMessage.text}
            </p>
        </div>
    );
};

const QuotedMessagePreview = ({ quotedMessage, onCancelQuote, agentNames }) => {
    if (!quotedMessage) return null;

    const senderName = quotedMessage.sender === 'agent' 
        ? (agentNames[quotedMessage.agentEmail] || 'Agente') 
        : (quotedMessage.contactName || 'Cliente'); 

    return (
        <div className="bg-gray-800 p-2 mx-2 mb-1 rounded-lg border-l-4 border-blue-400 relative animate-fade-in">
            <button 
                onClick={onCancelQuote} 
                className="absolute top-1 right-1 p-0.5 bg-gray-600 rounded-full text-white hover:bg-gray-500"
                title="Cancelar respuesta"
            >
                <X size={14} />
            </button>
            <p className="text-sm font-bold text-blue-300">{senderName}</p>
            <p className="text-sm text-gray-300 truncate">
                {quotedMessage.text || (quotedMessage.fileType?.startsWith('image') ? '🖼️ Imagen' : '📎 Archivo')}
            </p>
        </div>
    );
};

const MessageList = React.memo(({ messages, agentNames, onImageClick, searchTerm, onReplyClick, onQuoteClick }) => { 

    const highlightText = (text, term) => {
        if (!term || !text) { return text; }
        const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);
        return parts.map((part, index) =>
            regex.test(part) ? ( <mark key={index} className="bg-yellow-400 text-black px-0.5 rounded">{part}</mark> ) : ( part )
        );
    };

    const filteredMessages = useMemo(() => {
        if (!searchTerm) { return messages; }
        const lowerSearchTerm = searchTerm.toLowerCase();
        return messages.filter(msg => msg.text && msg.text.toLowerCase().includes(lowerSearchTerm));
    }, [messages, searchTerm]);

    const messagesWithSeparators = [];
    let lastDate = null;

    filteredMessages.forEach(msg => {
        if (msg.timestamp) {
            const currentDate = msg.timestamp.toDate();
            if (!lastDate || !isSameDay(lastDate, currentDate)) {
                messagesWithSeparators.push({ id: `sep_${currentDate.getTime()}`, type: 'separator', date: currentDate });
            }
            lastDate = currentDate;
        }
        messagesWithSeparators.push(msg);
    });

    return (
        <div className="space-y-4 pb-4"> {/* Añadido pb-4 para espacio extra abajo */}
            {messagesWithSeparators.map(msgOrSep => {
                if (msgOrSep.type === 'separator') {
                     return ( <div key={msgOrSep.id} className="text-center my-4"> <div className="inline-block px-3 py-1 text-xs font-semibold bg-gray-800 rounded-full">{formatDateSeparator(msgOrSep.date)}</div> </div> );
                }
                const msg = msgOrSep;
                return (
                    <div key={msg.id} id={`msg-${msg.id}`} className={`flex group ${msg.sender === 'agent' ? 'justify-end' : 'justify-start'} mb-3`}> {/* Añadido mb-3 para que las reacciones no se solapen con el siguiente mensaje */}
                        
                        {msg.sender === 'agent' && (
                            <button onClick={() => onReplyClick(msg)} title="Responder" className="p-2 text-gray-500 hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity self-start mr-1">
                                <CornerDownLeft size={16} />
                            </button>
                        )}
                        
                        {/* BURBUJA DEL MENSAJE */}
                        <div className={`p-3 rounded-lg max-w-lg relative shadow-sm ${msg.sender === 'agent' ? 'bg-hoprin-primary text-white' : 'bg-gray-800'}`}>
                            
                            <QuotedMessageDisplay quotedMessage={msg.quotedMessage} onQuoteClick={onQuoteClick} />

                            {msg.sender === 'agent' && <p className="text-xs font-bold text-white/80 mb-1">{agentNames[msg.agentEmail] || 'Agente'}</p>}
                            
                            {/* --- CORRECCIÓN VISUALIZACIÓN MULTIMEDIA --- */}
{msg.fileUrl && (msg.fileType === 'image' || msg.fileType?.startsWith('image/')) && (
    <img 
        src={msg.fileUrl} 
        alt={msg.fileName || "Imagen"} 
        className="rounded-md max-w-xs mb-2 cursor-pointer hover:opacity-90 transition-opacity" 
        onClick={() => onImageClick(msg.fileUrl)} 
    />
)}

{msg.fileUrl && (msg.fileType === 'video' || msg.fileType?.startsWith('video/')) && (
    <video 
        src={msg.fileUrl} 
        controls 
        className="rounded-md max-w-xs mb-2" 
    />
)}

{msg.fileUrl && (msg.fileType === 'audio' || msg.fileType?.startsWith('audio/')) && (
    <AudioPlayer src={msg.fileUrl} />
)}

{/* Fallback para documentos o tipos no reconocidos */}
{msg.fileUrl && 
 !msg.fileType?.startsWith('image') && 
 msg.fileType !== 'image' &&
 !msg.fileType?.startsWith('video') && 
 msg.fileType !== 'video' &&
 !msg.fileType?.startsWith('audio') && 
 msg.fileType !== 'audio' && (
    <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-gray-700 p-2 rounded-md hover:bg-gray-600 mb-1 text-blue-300 underline">
        <Paperclip size={16} /> 
        <span>{msg.fileName || 'Ver Archivo Adjunto'}</span> 
    </a>
)}
							
                            {msg.text && <div className='whitespace-pre-wrap break-words text-sm'>{highlightText(msg.text, searchTerm)}</div>}
                            
                            <div className="flex items-center justify-end gap-1 mt-1">
                                {msg.isEdited && <span className="text-[10px] italic text-white/60 mr-1">Editado</span>}
                                <span className="text-[10px] text-white/70">{formatTimestamp(msg.timestamp)}</span>
                                {msg.sender === 'agent' && <MessageStatus status={msg.status} />}
                            </div>

                            
                            {msg.reaction && (
                                <div className="absolute -bottom-2.5 right-0 translate-x-1/2 z-20 filter drop-shadow-sm animate-scale-in" style={{ pointerEvents: 'none' }}>
                                    <div className="bg-gray-700 text-white border-[2px] border-gray-900 rounded-full px-1.5 py-0.5 flex items-center justify-center min-w-[22px] h-[22px]">
                                        <span className="text-[10px] leading-none translate-y-[0.5px]">{msg.reaction}</span>
                                    </div>
                                </div>
                            )}

                        </div>

                        {msg.sender === 'contact' && (
                             <button onClick={() => onReplyClick(msg)} title="Responder" className="p-2 text-gray-500 hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity self-start ml-1">
                                <CornerDownLeft size={16} />
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
});

const DashboardKPI = ({ kpis }) => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"> {/* <-- MODIFICACIÓN AQUÍ */}
        {kpis.map(kpi => (
            <div key={kpi.title} className="bg-gray-800 p-4 rounded-lg shadow-md">
                <div className="flex items-center justify-between text-gray-400 text-sm">
                    <span>{kpi.title}</span>
                    <div className="relative group">
                        <Info size={16} />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-2 bg-black text-white text-xs text-center rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 pointer-events-none">
                            {kpi.tooltip}
                        </div>
                    </div>
                </div>
                <p className="text-3xl font-bold mt-2">{kpi.value}</p>
            </div>
        ))}
    </div>
);

// Archivo: App.js -> Reemplaza este componente
const DashboardAgentes = ({ agentsWithStats }) => {
    const availableCount = agentsWithStats.filter(a => a.status === 'Disponible').length;
    const awayCount = agentsWithStats.filter(a => a.status === 'Ausente').length;

    // Esta versión garantizada muestra todos los usuarios recibidos sin filtrar por rol.
    return (
        <div className="bg-gray-800 p-4 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Agentes</h2>
            <div>
                <div className="flex items-center gap-4 text-sm mb-4">
                    <span className="flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        {availableCount} Disponibles
                    </span>
                    <span className="flex items-center">
                        <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                        {awayCount} Ausentes
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="text-gray-400 border-b border-gray-700">
                                <th className="p-2">Agente</th>
                                <th className="p-2">Estado</th>
                                <th className="p-2">Chats Abiertos</th>
                            </tr>
                        </thead>
                        <tbody>
                            {agentsWithStats.map(agent => (
                                <tr key={agent.id} className="border-b border-gray-700">
                                    <td className="p-2">{agent.name}</td>
                                    <td className="p-2">
                                        <span className={`flex items-center ${agent.status === 'Disponible' ? 'text-green-400' : 'text-red-400'}`}>
                                            <span className={`w-2 h-2 ${agent.status === 'Disponible' ? 'bg-green-500' : 'bg-red-500'} rounded-full mr-2`}></span>
                                            {agent.status}
                                        </span>
                                    </td>
                                    <td className="p-2">{agent.openChats}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const MisNotasPanel = ({ currentAgent }) => {
    const [noteContent, setNoteContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const noteRef = useMemo(() => currentAgent?.uid ? doc(db, 'notes', currentAgent.uid) : null, [currentAgent]);
    const timeoutRef = useRef(null);

    useEffect(() => {
        if (!noteRef) return;
        const unsubscribe = onSnapshot(noteRef, (doc) => {
            if (doc.exists()) {
                setNoteContent(doc.data().content);
            } else {
                setNoteContent("");
            }
        });
        return () => unsubscribe();
    }, [noteRef]);

    const handleNoteChange = (e) => {
        const newText = e.target.value;
        setNoteContent(newText);
        setIsSaving(true);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(async () => {
            if (noteRef) {
                await setDoc(noteRef, { content: newText }, { merge: true });
                setIsSaving(false);
            }
        }, 1500);
    };
    
    return (
        <div className="bg-gray-800 p-4 rounded-lg shadow-md h-full flex flex-col">
            <div className="flex justify-between items-center mb-2">
                <h2 className="text-xl font-semibold">Mis Notas</h2>
            </div>
            <textarea
                value={noteContent}
                onChange={handleNoteChange}
                placeholder="Escribe tus notas aquí... Se guardan automáticamente."
                className="w-full h-full bg-gray-900/50 rounded-md p-2 text-sm text-gray-300 resize-none focus:ring-1 focus:ring-hoprin-primary focus:outline-none"
            ></textarea>
            <p className="text-xs text-gray-500 text-right h-4 mt-1">{isSaving ? 'Guardando...' : 'Guardado'}</p>
        </div>
    );
};


// Archivo: App.js -> Reemplaza este componente
const DashboardView = ({ agentsWithStats, chats, currentAgent, activityLogs, departments }) => {

    const atencionClienteDeptId = useMemo(() => {
        const dept = departments.find(d => d.name === 'Atención al Cliente');
        return dept ? dept.id : null;
    }, [departments]);

    const kpis = useMemo(() => {
        const filteredChatsForKPIs = atencionClienteDeptId
            ? chats.filter(c => c.departmentIds && c.departmentIds.includes(atencionClienteDeptId))
            : [];

        const PENDING_THRESHOLD_MS = 2 * 60 * 60 * 1000; // 2 horas
        const now = new Date();

        const openChats = filteredChatsForKPIs.filter(c => c.status === 'Abierto');
        const chatsWaitingForAgent = openChats.filter(c => c.lastMessageSender === 'contact' && c.lastMessageTimestamp?.toDate);
        
        const pendingChats = chatsWaitingForAgent.filter(c => {
            const timestampDate = c.lastMessageTimestamp?.toDate();
            // Asegurarse de que timestampDate sea una fecha válida antes de comparar
            return timestampDate instanceof Date && !isNaN(timestampDate) && (now - timestampDate) > PENDING_THRESHOLD_MS;
        });

        const calculateAvgWaitTime = (chatList) => {
            if (chatList.length === 0) return 0;
            const validChats = chatList.filter(chat => chat.lastMessageTimestamp?.toDate() instanceof Date && !isNaN(chat.lastMessageTimestamp.toDate()));
            if (validChats.length === 0) return 0; // Evitar división por cero si no hay timestamps válidos
            const totalWaitTime = validChats.reduce((sum, chat) => {
                 return sum + (now - chat.lastMessageTimestamp.toDate());
            }, 0);
            return totalWaitTime / validChats.length;
        };
        
        const avgOpenWaitTime = calculateAvgWaitTime(chatsWaitingForAgent);
        const avgPendingWaitTime = calculateAvgWaitTime(pendingChats);
        
        // --- INICIO DE LA MODIFICACIÓN (Títulos) ---
        return [
            { title: "Conversaciones Abiertas", value: openChats.length, tooltip: "Total de chats abiertos en Atención al Cliente." },
            { title: "Conversaciones Pendientes", value: chatsWaitingForAgent.length, tooltip: "Chats abiertos en Atención al Cliente esperando respuesta." },
            { title: "T. Prom. Espera", value: formatDuration(avgOpenWaitTime), tooltip: "Tiempo promedio desde el último mensaje del cliente en chats pendientes de Atención al Cliente." },
            { title: "T. Prom. Pendientes (+2h)", value: formatDuration(avgPendingWaitTime), tooltip: "Tiempo promedio de espera (>2h) en Atención al Cliente." },
        ];
        // --- FIN DE LA MODIFICACIÓN ---

    }, [chats, atencionClienteDeptId]);

    return (
        <div className="p-4 md:p-8 bg-gray-900 text-white h-full overflow-y-auto space-y-6">
            <DashboardKPI kpis={kpis} /> {/* KPIs actualizados */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                    <DashboardAgentes agentsWithStats={agentsWithStats} />
                </div>
                <div className="lg:col-span-1">
                    <MisNotasPanel currentAgent={currentAgent} />
                </div>
            </div>
        </div>
    );
};

const ChatListItem = React.memo(({ chat, isSelected, onSelect, agentName, notes, currentAgentEmail }) => {

    // Lógica para Indicador de Notas No Leídas (Sin cambios)
    const hasUnreadNotes = useMemo(() => {
        if (!notes || notes.length === 0) return false;
        // Verifica si ALGUNA nota NO incluye el email del agente actual en 'readBy'
        return notes.some(note => !note.readBy || !note.readBy.includes(currentAgentEmail));
    }, [notes, currentAgentEmail]);

    return (
        // --- INICIO DE LA MODIFICACIÓN (Borde de Selección) ---
        <div className={`w-full text-left flex items-center gap-3 border-b border-gray-700 hover:bg-gray-900/50 ${chat.status === 'Cerrado' ? 'opacity-60' : ''} ${isSelected ? 'bg-hoprin-accent/10 border-l-4 border-hoprin-primary py-4 pr-4 pl-3' : 'border-l-4 border-transparent p-4'}`}>
        {/*
           - Se añade 'border-l-4 border-transparent' al estado normal (para que el espacio esté reservado).
           - Se cambia a 'border-hoprin-primary' y se ajusta el padding izquierdo 'pl-3' al estado seleccionado.
           - Esto mantiene el texto alineado y añade el borde de color.
        */}
        {/* --- FIN DE LA MODIFICACIÓN --- */}

            {/* Botón principal */}
            <button onClick={() => onSelect(chat)} className="flex-grow flex items-center gap-3 min-w-0">
                
                {/* Avatar (sin cambios) */}
                <div className="relative shrink-0">
                     <Avatar internalId={chat.internalId} size="md" />
                </div>

                {/* Contenido del item */}
                <div className="flex-grow overflow-hidden">
                    {/* Fila superior: Nombre y Timestamp */}
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 min-w-0">
                            {/* Icono de plataforma */}
                            {chat.platform === 'telegram' ? <Send size={14} className="text-blue-400 shrink-0"/> : <Smartphone size={14} className="text-green-400 shrink-0"/>}
                            {/* Nombre */}
                            <p className="font-semibold truncate">{`[${chat.internalId}] ${chat.contactName}`}</p>
                            {/* Indicador de Nota (Sin cambios) */}
                            {hasUnreadNotes && (
                                <StickyNote size={12} className="text-yellow-400 shrink-0" title="Nuevas notas internas"/>
                            )}
                        </div>
                        {/* Timestamp */}
                        <p className="text-xs text-gray-400 shrink-0 ml-2">{formatTimestamp(chat.lastMessageTimestamp)}</p>
                    </div>
                    {/* Fila Agente */}
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                         {chat.agentEmail ? (
                             <>
                                 <UserCircle size={14} />
                                 <span className="truncate">{agentName || chat.agentEmail}</span>
                             </>
                         ) : (
                             chat.status === 'Abierto' && <span className="text-red-400 font-medium">¡Sin Asignar!</span>
                         )}
                     </div>
                     {/* Fila Etiquetas */}
                     <div className="flex flex-wrap gap-1 mt-1">
                        {chat.tags?.slice(0, 3).map((tag, index) => (
                            <span key={index} className={`text-xs ${TAG_COLORS[tag.color] || TAG_COLORS['gray']} text-white/90 rounded-full px-2 py-0.5`}>
                                {tag.name}
                            </span>
                        ))}
                     </div>
                </div>
            </button>
        </div>
    );
});

const ChatList = React.memo(({ chats, selectedChat, onSelectChat, agents, activeFilter, setActiveFilter, departmentFilter, setDepartmentFilter, currentAgent, departments, searchTerm, setSearchTerm, scrollContainerRef, onScroll, allInternalNotes }) => {
    const agentNames = useMemo(() => { const map = {}; agents.forEach(agent => { map[agent.email] = agent.name; }); return map; }, [agents]);
    
    return (
        <div className="bg-black border-r border-gray-700 flex flex-col h-full w-full">
            <div className="p-2 border-b border-gray-700 space-y-2 shrink-0">
                <div className="relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar chat..." className="w-full bg-gray-800 rounded-md p-2 pl-10 text-sm focus:ring-1 focus:ring-hoprin-primary focus:outline-none"/>
                </div>
                {/* ESTA ES LA SECCIÓN QUE CAMBIAMOS EN EL PASO 1 */}
                <div className="flex items-center gap-2">
                    <select 
						value={activeFilter} 
						onChange={(e) => setActiveFilter(e.target.value)} 
						className="w-full p-2 text-sm rounded-md bg-gray-800 border-transparent focus:ring-hoprin-primary"
					>
						<option value="abierto">Chats abiertos ({chats.filter(c => c.status === 'Abierto').length})</option>
						<option value="para_mi">Asignados a mí ({chats.filter(c => c.agentEmail === currentAgent.email && c.status === 'Abierto').length})</option>
						<option value="sin_asignar">Sin asignar ({chats.filter(c => !c.agentEmail && c.status === 'Abierto').length})</option>
						<option value="con_bot">Con bot ({chats.filter(c => c.isBotActive && c.status === 'Abierto').length})</option>

						<option value="cerrado">Chats cerrados</option>
						<option value="todos">Todos los chats</option>
					</select>

                </div>
                {(currentAgent.role === 'gerente' || currentAgent.role === 'administrador') && (
                     <select value={departmentFilter} onChange={(e) => { setDepartmentFilter(e.target.value); setActiveFilter('todos'); }} className="w-full p-2 text-sm rounded-md bg-gray-800 border-transparent focus:ring-hoprin-primary">
                        <option value="todos">Todos los departamentos</option>
                        {departments.map(dept => <option key={dept.id} value={dept.id}>{dept.name}</option>)}
                    </select>
                )}
            </div>
            {/* --- INICIO DE LA CORRECCIÓN --- */}
            {/* Añadimos el ref y el onScroll al div que realmente tiene el scroll */}
            <div ref={scrollContainerRef} onScroll={onScroll} className="flex-grow overflow-y-auto gpu-layer">
                {chats.map(chat => ( <ChatListItem key={chat.id} chat={chat} isSelected={selectedChat?.id === chat.id} onSelect={onSelectChat} agentName={agentNames[chat.agentEmail]} notes={allInternalNotes[chat.id] || []} currentAgentEmail={currentAgent.email}/> ))}
            </div>
            {/* --- FIN DE LA CORRECCIÓN --- */}
        </div>
    );
});

const ChatProfile = React.memo(({ selectedChat, onToggleProfile, services, onMobileBack, currentAgent }) => {
    const [isEditingName, setIsEditingName] = useState(false);
    const [editableName, setEditableName] = useState("");
    const [isEditingId, setIsEditingId] = useState(false);
    const [editableId, setEditableId] = useState("");
    const [isEditingNote, setIsEditingNote] = useState(false);
    const [editableNote, setEditableNote] = useState("");
    const [activeServices, setActiveServices] = useState([]);
    const [newTag, setNewTag] = useState({ name: '', color: 'gray' });

    const contactIdentifier = useMemo(() => {
        if (!selectedChat) return '';
        if (selectedChat.platform === 'telegram') {
            return selectedChat.telegramUsername ? `@${selectedChat.telegramUsername}` : selectedChat.contactId;
        }
        return selectedChat.contactPhone ? selectedChat.contactPhone.split('@')[0] : '';
    }, [selectedChat]);

    useEffect(() => {
        if (selectedChat) {
            setEditableName(selectedChat.contactName);
            setEditableId(selectedChat.internalId || "");
            setEditableNote(selectedChat.contactNote || "");
            setActiveServices(selectedChat.activeServices || []);
            setIsEditingName(false);
            setIsEditingId(false);
            setIsEditingNote(false);
        }
    }, [selectedChat]);

    const handleSave = async (field, value) => { if (!selectedChat) return; await updateDoc(doc(db, 'chats', selectedChat.id), { [field]: value }); if(field === 'contactName') setIsEditingName(false); if(field === 'internalId') setIsEditingId(false); if(field === 'contactNote') setIsEditingNote(false); };
    const handleNoteDelete = async () => { if (!selectedChat) return; await updateDoc(doc(db, 'chats', selectedChat.id), { contactNote: "" }); setEditableNote(""); setIsEditingNote(false); };

    const handleServiceToggle = async (serviceId) => {
        if (!selectedChat) return;
        const updatedServices = activeServices.includes(serviceId) ? activeServices.filter(id => id !== serviceId) : [...activeServices, serviceId];
        setActiveServices(updatedServices);
        await updateDoc(doc(db, 'chats', selectedChat.id), { activeServices: updatedServices });
    };

    const handleAddTag = async () => {
        if (!selectedChat || !newTag.name.trim()) return;
        const tagToAdd = { name: newTag.name.trim(), color: newTag.color };
        await updateDoc(doc(db, 'chats', selectedChat.id), { tags: arrayUnion(tagToAdd) });
        setNewTag({ name: '', color: 'gray' });
    };

    const handleRemoveTag = async (tagToRemove) => {
        if (!selectedChat) return;
        await updateDoc(doc(db, 'chats', selectedChat.id), { tags: arrayRemove(tagToRemove) });
    };

    const canViewPhoneNumber = !(currentAgent.role === 'agente' && currentAgent.department === 'Gestión de Cuentas');

    if (!selectedChat) return null;

    return (
        <div className="bg-black border-l border-gray-700 flex flex-col w-full h-full relative gpu-layer">
            <div className='flex items-center p-4 border-b border-gray-700 md:hidden'>
                <button onClick={onMobileBack} className="text-gray-400 hover:text-white mr-4"><ArrowLeft size={24}/></button>
                <h2 className='font-bold text-lg'>Información del Contacto</h2>
            </div>
            <button onClick={onToggleProfile} className="absolute top-4 right-4 text-gray-400 hover:text-white z-10 hidden md:block"><X size={24}/></button>
            <div className="p-4 text-center border-b border-gray-700 mt-8 md:mt-8">
                <div className="mx-auto mb-4 inline-block"><Avatar internalId={selectedChat.internalId} size="lg" /></div>
                {isEditingName ? ( <div className="flex gap-2 justify-center"> <input type="text" value={editableName} onChange={(e) => setEditableName(e.target.value)} className="w-full p-1 border rounded-md bg-gray-700 text-center"/> <button onClick={() => handleSave('contactName', editableName)} className="p-2 bg-green-500 text-white rounded-md hover:bg-green-600"><Save size={16}/></button> </div> ) : ( <div className="flex gap-2 justify-center items-center"> <h3 className="text-xl font-bold">{selectedChat.contactName}</h3> <button onClick={() => setIsEditingName(true)} className="text-gray-400 hover:text-white"><Pencil size={16}/></button> </div> )}
                {isEditingId ? ( <div className="flex gap-2 justify-center mt-2"> <input type="text" value={editableId} onChange={(e) => setEditableId(e.target.value)} className="w-full p-1 border rounded-md bg-gray-700 text-center"/> <button onClick={() => handleSave('internalId', editableId)} className="p-2 bg-green-500 text-white rounded-md hover:bg-green-600"><Save size={16}/></button> </div> ) : ( <div className="flex gap-2 justify-center items-center mt-1"> <p className="text-base text-gray-400">ID: {selectedChat.internalId}</p> <button onClick={() => setIsEditingId(true)} className="text-gray-400 hover:text-white"><Pencil size={12}/></button> </div> )}

                {canViewPhoneNumber && (
                    <div className="flex gap-2 justify-center items-center mt-1">
                        <p className="text-base text-gray-400">{contactIdentifier}</p>
                        <button onClick={() => navigator.clipboard.writeText(contactIdentifier)} title="Copiar Identificador" className="text-gray-400 hover:text-white"><Copy size={12}/></button>
                    </div>
                )}
            </div>
            <div className="p-4 space-y-6 flex-grow overflow-y-auto">
                <div>
                    <h4 className="font-bold text-sm mb-2 flex items-center gap-2"><Tag size={16}/> Etiquetas</h4>
                    <div className="flex flex-wrap gap-2 mb-3">
                        {selectedChat.tags?.map((tag, index) => (
                            <span key={index} className={`flex items-center gap-1 text-xs ${TAG_COLORS[tag.color] || TAG_COLORS['gray']} text-white/90 rounded-full px-2 py-1`}>
                                {tag.name}
                                <button onClick={() => handleRemoveTag(tag)} className="ml-1 opacity-70 hover:opacity-100"><X size={12}/></button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                         <input type="text" placeholder="Nueva etiqueta..." value={newTag.name} onChange={(e) => setNewTag({...newTag, name: e.target.value})} className="flex-grow p-1 text-sm border rounded-md bg-gray-700"/>
                         <select value={newTag.color} onChange={(e) => setNewTag({...newTag, color: e.target.value})} className="p-1 text-sm border rounded-md bg-gray-700">
                             {Object.keys(TAG_COLORS).map(color => <option key={color} value={color}>{color.charAt(0).toUpperCase() + color.slice(1)}</option>)}
                         </select>
                         <button onClick={handleAddTag} className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"><PlusCircle size={16}/></button>
                    </div>
                </div>
                <div>
                    <div className="flex justify-between items-center mb-2"> <h4 className="font-bold text-sm flex items-center gap-2"><StickyNote size={16}/> Nota de Contacto</h4> {!isEditingNote && <button onClick={() => setIsEditingNote(true)} className="text-gray-400 hover:text-white"><Pencil size={16}/></button>} </div>
                    {isEditingNote ? ( <div className="space-y-2"> <textarea value={editableNote} onChange={(e) => setEditableNote(e.target.value)} rows="4" className="w-full p-2 text-sm border rounded-md bg-gray-700"></textarea> <div className="flex gap-2"> <button onClick={() => handleSave('contactNote', editableNote)} className="p-2 bg-green-500 text-white rounded-md hover:bg-green-600"><Save size={16}/></button> <button onClick={handleNoteDelete} className="p-2 bg-red-500 text-white rounded-md hover:bg-red-600"><Trash size={16}/></button> <button onClick={() => setIsEditingNote(false)} className="p-2 bg-gray-500 text-white rounded-md hover:bg-gray-600">Cancelar</button> </div> </div> ) : ( <p className="text-sm text-gray-300 bg-gray-800 p-2 rounded-md min-h-[50px]">{selectedChat.contactNote || "No hay nota."}</p> )}
                </div>

                {currentAgent.department !== 'Gestión de Cuentas' && (
                    <div>
                        <h4 className="font-bold text-sm mb-2 flex items-center gap-2"><Briefcase size={16}/> Servicios Contratados</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                           {services.map(service => (
                               <label key={service.id} className="flex items-center gap-3 p-2 bg-gray-800 rounded-md cursor-pointer hover:bg-gray-700">
                                   <input type="checkbox" checked={activeServices.includes(service.id)} onChange={() => handleServiceToggle(service.id)} className="form-checkbox h-5 w-5 bg-gray-900 border-gray-600 text-hoprin-primary focus:ring-hoprin-primary" />
                                   <span className="text-sm">{service.name}</span>
                                </label>
                           ))}
                        </div>
                    </div>
                )}

                {/* --- INICIO DE LA MODIFICACIÓN (Calificación) --- */}
                <div>
                    <h4 className="font-bold text-sm mb-2 flex items-center gap-2"><Star size={16}/> Calificación de la Conversación</h4>
                     <div className="bg-gray-800 p-3 rounded-md text-center">
                         {selectedChat.status === 'Cerrado' ? (
                             selectedChat.rating === 'positive' ? (
                                 <div className="flex justify-center items-center gap-2 text-green-400">
                                     <ThumbsUp size={24} />
                                     <span>Positiva</span>
                                 </div>
                             ) : selectedChat.rating === 'negative' ? (
                                 <div className="flex justify-center items-center gap-2 text-red-400">
                                     <ThumbsDown size={24} />
                                     <span>Negativa</span>
                                 </div>
                             ) : ( // Si no es positive/negative (o es null/undefined)
                                 <p className="text-gray-400 italic text-sm">Calificación pendiente...</p>
                             )
                         ) : ( // Si el chat no está cerrado
                              <p className="text-gray-500 italic text-sm">La calificación estará disponible cuando se cierre el chat.</p>
                         )}
                     </div>
                </div>
                {/* --- FIN DE LA MODIFICACIÓN --- */}

            </div>
        </div>
    );
});

const QuickReplyPopup = ({ replies, onSelect, search }) => {
    const filteredReplies = useMemo(() => {
        const searchTerm = search.substring(1);
        if (!searchTerm) return replies;
        return replies.filter(reply =>
            reply.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            reply.text.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [search, replies]);
    if(filteredReplies.length === 0) return null;

    return (
        <div className="absolute bottom-20 left-4 w-80 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-20 flex flex-col">
            <div className="p-2 bg-black text-white rounded-t-lg font-bold text-sm">Respuestas Rápidas</div>
            <div className="max-h-60 overflow-y-auto">
                {filteredReplies.map(reply => (
                    <button
                        key={reply.id}
                        onClick={() => onSelect(reply.text)}
                        className="w-full text-left p-3 hover:bg-gray-800 border-b border-gray-800"
                    >
                         <p className="font-bold text-sm">/{reply.title}</p>
                        <p className="text-xs text-gray-400 truncate">{reply.text}</p>
                    </button>
                ))}
            </div>
        </div>
    );
};

const InternalNoteList = ({ notes, agentNames }) => {
    if (!notes || notes.length === 0) {
        return <p className="text-center text-gray-500 italic p-4">No hay notas internas todavía.</p>;
    }

    const sortedNotes = [...notes].sort((a, b) => (a.timestamp?.toDate() || 0) - (b.timestamp?.toDate() || 0));

    return (
        <div className="space-y-3 p-4">
            {sortedNotes.map(note => (
                <div key={note.id} className="bg-gray-800 p-3 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-bold text-hoprin-accent">
                            {agentNames[note.agentEmail] || note.agentEmail.split('@')[0]}
                        </span>
                        <span className="text-xs text-gray-400">
                            {formatTimestamp(note.timestamp)}
                        </span>
                    </div>
                    <p className="text-sm text-gray-200 whitespace-pre-wrap">{note.text}</p>
                </div>
            ))}
        </div>
    );
};

const ChatHistoryModal = ({ chat, onClose }) => {
    const [historyMessages, setHistoryMessages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadHistory = async () => {
            try {
                // Cargamos de la colección de archivo (creada por la Cloud Function)
                const q = query(collection(db, `chats/${chat.id}/archived_messages`), orderBy('timestamp', 'asc'), limit(100));
                const snapshot = await getDocs(q);
                setHistoryMessages(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (error) {
                console.error("Error cargando historial:", error);
            } finally {
                setLoading(false);
            }
        };
        loadHistory();
    }, [chat.id]);

    return (
        <Modal onClose={onClose} size="lg">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <ArchiveRestore size={24} className="text-yellow-500"/> Historial Archivado (Más de 3 meses)
            </h2>
            <div className="bg-gray-900 rounded-lg p-4 h-96 overflow-y-auto space-y-3">
                {loading ? (
                    <p className="text-center text-gray-400">Cargando archivo...</p>
                ) : historyMessages.length === 0 ? (
                    <p className="text-center text-gray-500">No hay mensajes antiguos archivados para este chat.</p>
                ) : (
                    historyMessages.map(msg => (
                        <div key={msg.id} className={`p-2 rounded-lg max-w-[80%] text-sm ${msg.sender === 'agent' ? 'bg-gray-700 ml-auto text-right' : 'bg-black border border-gray-700'}`}>
                            <p className="font-bold text-xs text-gray-400 mb-1">{formatTimestamp(msg.timestamp)} - {msg.sender === 'agent' ? 'Agente' : 'Cliente'}</p>
                            <p>{msg.text || (msg.fileUrl ? '📎 Archivo adjunto' : '')}</p>
                        </div>
                    ))
                )}
            </div>
            <div className="mt-4 text-right">
                <button onClick={onClose} className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-500">Cerrar</button>
            </div>
        </Modal>
    );
};

const ChatWindow = ({ selectedChat, setSelectedChat, agents, departments, onToggleProfile, onCloseChat, waSocket, tgSocket, currentAgent, onMobileBack, quickReplies, onImageClick, initialChatTab }) => {
    
    // --- ESTADOS ---
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [newMessage, setNewMessage] = useState("");
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showQuickReplies, setShowQuickReplies] = useState(false);
    const [quickReplySearch, setQuickReplySearch] = useState('');
    const [showReassignModal, setShowReassignModal] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [chatSearchTerm, setChatSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(initialChatTab || 'chat');
    const [internalNotes, setInternalNotes] = useState([]);
    const [newInternalNote, setNewInternalNote] = useState('');
    const [isSendingNote, setIsSendingNote] = useState(false);
    const [showMentionPopup, setShowMentionPopup] = useState(false);
    const [mentionSearchTerm, setMentionSearchTerm] = useState('');
    const [quotedMessage, setQuotedMessage] = useState(null);
    
    // --- NUEVOS ESTADOS PARA ADJUNTOS ---
    const [attachment, setAttachment] = useState(null); // El archivo en sí
    const [attachmentPreview, setAttachmentPreview] = useState(null); // URL para mostrarlo
    const [isUploading, setIsUploading] = useState(false); // Spinner de subida

    // --- NUEVO: Estado para paginación ---
    const [messageLimit, setMessageLimit] = useState(50); 
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // --- REFS ---
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const chatContainerRef = useRef(null);
    const internalNotesContainerRef = useRef(null);
    const isScrolledByUserRef = useRef(false);
    const mentionPopupRef = useRef(null);
    const noteTextareaRef = useRef(null);

    // --- MEMOS (Sin cambios) ---
    const agentNames = useMemo(() => {
        const map = {};
        agents.forEach(agent => { map[agent.email] = agent.name.split(' ')[0]; });
        return map;
    }, [agents]);

    const chatDepartmentName = useMemo(() => {
        if (!selectedChat || !departments || !selectedChat.departmentIds) return null;
        const dept = departments.find(d => selectedChat.departmentIds.includes(d.id));
        return dept ? dept.name : null;
     }, [departments, selectedChat]);

     const availableAgents = useMemo(() => {
        if (!chatDepartmentName) return [];
        return agents.filter(agent =>
            agent.status === 'Disponible' &&
            agent.email !== currentAgent.email &&
            (agent.role === 'agente' || agent.role === 'supervisor') &&
            agent.department === chatDepartmentName
        );
     }, [agents, chatDepartmentName, currentAgent.email]);
     
    const mentionableAgents = useMemo(() => {
        const globalRoles = ['gerente', 'administrador'];
        const agentDepartment = currentAgent.department;
        return agents.filter(agent => {
            if (agent.email === currentAgent.email) { return false; }
            if (globalRoles.includes(agent.role)) { return true; }
            return agent.department === agentDepartment;
        });
    }, [agents, currentAgent.email, currentAgent.department]);

    const filteredMentionableAgents = useMemo(() => {
        if (!mentionSearchTerm) return mentionableAgents;
        const lowerSearch = mentionSearchTerm.toLowerCase();
        return mentionableAgents.filter(agent =>
            agent.name.toLowerCase().includes(lowerSearch) ||
            agent.email.toLowerCase().includes(lowerSearch)
        );
    }, [mentionableAgents, mentionSearchTerm]);

    // --- EFFECTS ---
    useEffect(() => { 
        if (!selectedChat?.id) { setMessages([]); setInternalNotes([]); setIsLoading(false); return; }
        
        setMessageLimit(50); 
        setIsLoading(true); 
        isScrolledByUserRef.current = false; 
        setIsSearching(false); 
        setChatSearchTerm('');
        setQuotedMessage(null);
        
        // Limpiar adjuntos al cambiar de chat
        setAttachment(null);
        setAttachmentPreview(null);
        setNewMessage("");

    }, [selectedChat?.id]);

    useEffect(() => {
        if (!selectedChat?.id) return;

        const msgQuery = query(
            collection(db, `chats/${selectedChat.id}/messages`), 
            orderBy('timestamp', 'asc'),
            limitToLast(messageLimit) 
        );

        const notesQuery = query(collection(db, `chats/${selectedChat.id}/internal_notes`), orderBy('timestamp', 'asc'));
        
        const unsubMsg = onSnapshot(msgQuery, (s) => { 
            setMessages(s.docs.map(d => ({ id: d.id, ...d.data() }))); 
            setIsLoading(false); 
            setIsLoadingMore(false); 
        }, (e) => { console.error("Error listener mensajes:", e); setIsLoading(false); });
        
        const unsubNotes = onSnapshot(notesQuery, (s) => setInternalNotes(s.docs.map(d => ({ id: d.id, ...d.data() }))), (e) => console.error("Error listener notas:", e));
        
        return () => { unsubMsg(); unsubNotes(); };
    }, [selectedChat?.id, messageLimit]);

    useEffect(() => { 
        const container = chatContainerRef.current;
        if (activeTab === 'chat' && container && !isScrolledByUserRef.current && !isLoadingMore) {
            container.scrollTop = container.scrollHeight;
        }
    }, [messages, chatSearchTerm, activeTab, isLoadingMore]);

    useEffect(() => { 
        const container = internalNotesContainerRef.current;
         if (activeTab === 'notes' && container) container.scrollTop = container.scrollHeight;
    }, [internalNotes, activeTab]);

    useEffect(() => { 
        const textarea = inputRef.current; if (textarea) { textarea.style.height = 'auto'; textarea.style.height = `${textarea.scrollHeight}px`; }
    }, [newMessage]);
    
    useEffect(() => { 
        if (activeTab === 'notes' && tgSocket && currentAgent && internalNotes.length > 0) {
            const unreadNoteIds = internalNotes.filter(n => !n.readBy || !n.readBy.includes(currentAgent.email)).map(n => n.id);
            if (unreadNoteIds.length > 0) {
                tgSocket.emit('marcar_notas_leidas', { chatId: selectedChat.id, agentEmail: currentAgent.email, noteIds: unreadNoteIds });
            }
        }
    }, [activeTab, internalNotes, tgSocket, currentAgent, selectedChat?.id]);


    const handleScroll = () => {
        const container = chatContainerRef.current; 
        if (container) { 
            const isNearBottom = container.scrollHeight - container.clientHeight <= container.scrollTop + 150; 
            isScrolledByUserRef.current = !isNearBottom; 
        }
    };

    const loadMoreMessages = () => {
        setIsLoadingMore(true);
        setMessageLimit(prev => prev + 50);
    };
    
    // --- NUEVA LÓGICA DE ENVÍO ---
    const handleLocalSendMessage = async () => {
        if (!newMessage.trim() && !quotedMessage && !attachment) return; // Validar si hay algo que enviar
        if (!selectedChat || (!waSocket && !tgSocket)) return; 

        isScrolledByUserRef.current = false;
        
        // Si hay un archivo adjunto, usamos la función de upload
        if (attachment) {
            await uploadFile(attachment, newMessage); // Pasamos el texto como caption
            // Limpiamos estados después de subir
            setAttachment(null);
            setAttachmentPreview(null);
            setNewMessage("");
            setQuotedMessage(null);
            setShowEmojiPicker(false);
            return;
        }

        // Si es solo texto
        let quoteInfo = null;
        if (quotedMessage) {
            quoteInfo = {
                id: quotedMessage.id,
                text: quotedMessage.text || (quotedMessage.fileType ? 'Archivo' : ''),
                sender: quotedMessage.sender,
            };
        }

        const payload = { 
            chatId: selectedChat.id, 
            message: newMessage, 
            agentEmail: currentAgent.email,
            quotedMessage: quoteInfo
        };

        if (selectedChat.platform === 'whatsapp' && waSocket) {
            waSocket.emit('enviar_mensaje', payload);
        } else if (selectedChat.platform === 'telegram' && tgSocket) {
            tgSocket.emit('enviar_mensaje', payload);
        }
        
        setNewMessage(""); 
        setShowEmojiPicker(false);
        setQuotedMessage(null);
    };

    const onEmojiClick = (emojiObject) => { setNewMessage(prev => prev + emojiObject.emoji); inputRef.current?.focus(); };
    
    const handleInputChange = (e) => {
        const value = e.target.value; setNewMessage(value); const slashIndex = value.lastIndexOf('/');
        if (slashIndex !== -1 && !value.substring(slashIndex + 1).includes(' ')) { setShowQuickReplies(true); setQuickReplySearch(value.substring(slashIndex)); } else { setShowQuickReplies(false); }
    };
    
    const onQuickReplySelect = (text) => { const slashIndex = newMessage.lastIndexOf('/'); setNewMessage(newMessage.substring(0, slashIndex) + text); setShowQuickReplies(false); inputRef.current?.focus(); };
    
    // --- NUEVA FUNCIÓN: PREPARAR ARCHIVO (NO SUBIR AÚN) ---
    const handleSelectFile = (file) => {
        if (!file) return;
        
        // Crear URL de vista previa
        const preview = URL.createObjectURL(file);
        setAttachment(file);
        setAttachmentPreview(preview);
        
        // Enfocar el input para que escriban el caption
        inputRef.current?.focus();
    };

    const handleFileChange = (e) => { 
        const file = e.target.files[0]; 
        if (file) handleSelectFile(file); 
        if (e.target) e.target.value = null; 
    };
    
    const handlePaste = (e) => {
        const items = e.clipboardData.items; 
        for (const item of items) { 
            if (item.type.indexOf('image') !== -1) { 
                const file = item.getAsFile(); 
                handleSelectFile(file); // Usar la nueva función
                e.preventDefault(); 
                return; 
            } 
        }
    };

    const handleRemoveAttachment = () => {
        setAttachment(null);
        setAttachmentPreview(null);
    };

    // --- FUNCIÓN UPLOAD MODIFICADA PARA ACEPTAR CAPTION ---
    const uploadFile = async (file, caption = '') => {
        if (!file || (!waSocket && !tgSocket) || !selectedChat) return; 
        
        setIsUploading(true); // Mostrar spinner visual si quisieras implementarlo
        const formData = new FormData(); 
        formData.append('file', file);
        
        try { 
            const UPLOAD_BACKEND_URL = window.location.hostname === 'localhost' ? "http://localhost:3001" : "https://hoprinplus-backend.onrender.com"; 
            const resp = await fetch(`${UPLOAD_BACKEND_URL}/upload`, { method: 'POST', body: formData });
            
            if (!resp.ok) throw new Error(`Error en la subida: ${resp.status}`);
            
            const data = await resp.json();
            
            // Aquí enviamos el mensaje con el caption incluido
            const payload = { 
                chatId: selectedChat.id, 
                message: caption, // <-- AHORA USAMOS EL CAPTION
                agentEmail: currentAgent.email, 
                fileUrl: data.url, 
                fileType: data.mimetype, 
                fileName: data.name 
            };
            
            if (selectedChat.platform === 'whatsapp' && waSocket) waSocket.emit('enviar_mensaje', payload);
            else if (selectedChat.platform === 'telegram' && tgSocket) tgSocket.emit('enviar_mensaje', payload);
            
        } catch (error) { 
            console.error("Error al subir el archivo:", error); 
            alert(`Error al subir el archivo: ${error.message}`); 
        } finally {
            setIsUploading(false);
        }
    };

    const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleLocalSendMessage(); } };
    
    const handleReassignChat = async (agentEmail) => {
        if (!selectedChat) return; try { await updateDoc(doc(db, 'chats', selectedChat.id), { agentEmail: agentEmail }); setShowReassignModal(false); } catch (error) { console.error("Error al reasignar:", error); alert("No se pudo reasignar."); setShowReassignModal(false); }
    };

    const handleReplyClick = (messageToQuote) => { setQuotedMessage(messageToQuote); inputRef.current?.focus(); };
    
    const handleQuoteClick = (messageId) => {
        const messageElement = document.getElementById(`msg-${messageId}`);
        if (messageElement) {
            messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const originalBg = messageElement.style.backgroundColor;
            messageElement.style.backgroundColor = '#312E81';
            messageElement.style.transition = 'background-color 0.5s ease';
            setTimeout(() => { messageElement.style.backgroundColor = originalBg; }, 2000);
        } else {
            alert("El mensaje citado es antiguo y no está en pantalla. Intenta cargar mensajes anteriores.");
        }
    };
    
    const handleInternalNoteChange = (e) => {
        const text = e.target.value; setNewInternalNote(text); const cursorPos = e.target.selectionStart; const textBeforeCursor = text.substring(0, cursorPos); const atIndex = textBeforeCursor.lastIndexOf('@');
        if (atIndex !== -1 && textBeforeCursor.substring(atIndex + 1).indexOf(' ') === -1) { const searchTerm = textBeforeCursor.substring(atIndex + 1); setMentionSearchTerm(searchTerm); setShowMentionPopup(true); } else { setShowMentionPopup(false); }
    };
    const handleMentionSelect = (agent) => {
        const text = newInternalNote; const cursorPos = noteTextareaRef.current.selectionStart; const textBeforeCursor = text.substring(0, cursorPos); const atIndex = textBeforeCursor.lastIndexOf('@');
        const newText = text.substring(0, atIndex) + `@${agent.name} ` + text.substring(cursorPos);
        setNewInternalNote(newText); setShowMentionPopup(false);
        noteTextareaRef.current.focus();
        setTimeout(() => { noteTextareaRef.current.selectionStart = noteTextareaRef.current.selectionEnd = atIndex + agent.name.length + 2; }, 0);
    };
    
    const handleSendInternalNote = () => {
        if (!newInternalNote.trim() || !selectedChat || !tgSocket || !currentAgent) { alert("Error: No se pudo conectar al servidor de notas."); return; }
        setIsSendingNote(true);
        tgSocket.emit('guardar_nota_interna', { chatId: selectedChat.id, agentEmail: currentAgent.email, noteText: newInternalNote });
        tgSocket.once('nota_interna_error', ({ chatId, error }) => { if (chatId === selectedChat.id) { alert(`Error al guardar nota: ${error}`); setIsSendingNote(false); } });
        setNewInternalNote('');
        setIsSendingNote(false);
    };
    
    const handleInternalNoteKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!showMentionPopup) { handleSendInternalNote(); } } };

    if (!selectedChat) {
        return (
            <div className="bg-black flex-grow flex flex-col justify-center items-center text-gray-500 h-full">
                <MessageSquareText size={64} className="mb-4"/>
                <h2 className="text-xl font-semibold">Selecciona un chat</h2>
                <p>Elige una conversación de la lista para empezar.</p>
            </div>
        );
    }

    return (
        <div className="bg-black flex flex-col h-full w-full gpu-layer relative">
            
            {showHistory && <ChatHistoryModal chat={selectedChat} onClose={() => setShowHistory(false)} />}
            
            {showReassignModal && (
                <div className="absolute inset-0 bg-black/70 flex justify-center items-center z-20">
                    <div className="bg-gray-800 p-4 rounded-lg w-80 shadow-lg">
                        <h3 className="font-bold mb-4 text-white">Reasignar Chat</h3>
                        <select onChange={(e) => handleReassignChat(e.target.value)} defaultValue="" className="w-full p-2 border border-gray-600 rounded-md bg-gray-700 text-white">
                            <option value="" disabled>Selecciona un agente disponible</option>
                            {availableAgents.map(agent => <option key={agent.id} value={agent.email}>{agent.name}</option>)}
                        </select>
                        <button onClick={() => setShowReassignModal(false)} className="mt-4 w-full p-2 bg-gray-600 rounded-md text-white hover:bg-gray-500">Cancelar</button>
                    </div>
                </div>
            )}

            <header className="bg-gray-900 p-3 flex items-center justify-between border-b border-gray-700 shrink-0">
                {isSearching ? (
                    <div className="flex-1 flex items-center gap-2">
                        <Search size={20} className="text-gray-500 shrink-0" />
                        <input type="text" value={chatSearchTerm} onChange={(e) => setChatSearchTerm(e.target.value)} placeholder="Buscar en este chat..." className="w-full bg-gray-700 text-white rounded-md p-1.5 text-sm focus:ring-1 focus:ring-hoprin-primary focus:outline-none" autoFocus />
                        <button onClick={() => { setIsSearching(false); setChatSearchTerm(''); }} className="text-gray-400 hover:text-white"><X size={20} /></button>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-3 min-w-0">
                            <button onClick={onMobileBack} className="md:hidden p-1 text-gray-400"><ArrowLeft size={22}/></button>
                            <Avatar internalId={selectedChat.internalId} size="md" />
                            <div className="flex items-center gap-2 min-w-0">
                                <h2 className="font-bold text-lg truncate">{selectedChat.contactName}</h2>
                                {selectedChat.platform === 'telegram' ? <Send size={16} className="text-blue-400 shrink-0"/> : <Smartphone size={16} className="text-green-400 shrink-0"/> }
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <button onClick={() => setShowHistory(true)} title="Ver Historial Antiguo" className="text-yellow-500 hover:text-yellow-400">
                                <ArchiveRestore size={20}/>
                            </button>
                            
                            <button onClick={() => setIsSearching(true)} title="Buscar en Chat" className="text-gray-400 hover:text-white"><Search size={20}/></button>
                            <button onClick={() => { if (availableAgents.length > 0) { setShowReassignModal(true); } else { alert("No hay otros agentes disponibles..."); }}} title="Reasignar Chat" className="text-gray-400 hover:text-white"><UsersIcon size={20}/></button>
                            <button onClick={() => onCloseChat(selectedChat)} title={selectedChat.status === 'Abierto' ? 'Cerrar Chat' : 'Reabrir Chat'} className={`text-white hover:text-white px-2 py-1 rounded-md transition-colors ${selectedChat.status === 'Abierto' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
                                {selectedChat.status === 'Abierto' ? <Archive size={20}/> : <ArchiveRestore size={20}/>}
                            </button>
                            <button onClick={onToggleProfile} title="Ver Perfil" className="text-gray-400 hover:text-white"><UserCircle size={20}/></button>
                        </div>
                    </>
                )}
            </header>

            <div className="flex border-b border-gray-700 shrink-0">
                <button onClick={() => setActiveTab('chat')} className={`flex-1 py-2 text-center text-sm font-medium ${activeTab === 'chat' ? 'text-hoprin-primary border-b-2 border-hoprin-primary' : 'text-gray-400 hover:text-white'}`}>Chat</button>
                <button onClick={() => setActiveTab('notes')} className={`flex-1 py-2 text-center text-sm font-medium ${activeTab === 'notes' ? 'text-hoprin-primary border-b-2 border-hoprin-primary' : 'text-gray-400 hover:text-white'}`}>Nota Interna</button>
            </div>

            {activeTab === 'chat' ? (
                <>
                    <div ref={chatContainerRef} onScroll={handleScroll} className="flex-grow overflow-y-auto p-4">
                        {messages.length >= messageLimit && !isLoading && (
                            <div className="text-center mb-4">
                                <button 
                                    onClick={loadMoreMessages} 
                                    disabled={isLoadingMore}
                                    className="text-xs bg-gray-800 hover:bg-gray-700 text-blue-400 py-1 px-3 rounded-full transition-colors"
                                >
                                    {isLoadingMore ? 'Cargando...' : 'Cargar mensajes anteriores'}
                                </button>
                            </div>
                        )}
                        
                        {isLoading ? ( <div className="flex justify-center items-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-hoprin-primary"></div></div> ) : ( 
                            <MessageList 
                                messages={messages} 
                                agentNames={agentNames} 
                                onImageClick={onImageClick} 
                                searchTerm={chatSearchTerm} 
                                onReplyClick={handleReplyClick}
                                onQuoteClick={handleQuoteClick}
                            /> 
                        )}
                    </div>
                    {showQuickReplies && <QuickReplyPopup replies={quickReplies} search={quickReplySearch} onSelect={onQuickReplySelect} />}
                    {showEmojiPicker && <div className="absolute bottom-20 z-10"><EmojiPicker onEmojiClick={onEmojiClick} theme="dark" /></div>}
                    
                    <form onSubmit={(e) => { e.preventDefault(); handleLocalSendMessage(); }} className="bg-black p-2 flex flex-col border-t border-gray-700 shrink-0">
                        
                        {/* VISTA PREVIA DE ARCHIVO ADJUNTO (NUEVO) */}
                        {attachmentPreview && (
                            <div className="bg-gray-800 p-2 mx-2 mb-2 rounded-lg border border-gray-600 flex items-center justify-between animate-fade-in">
                                <div className="flex items-center gap-3 overflow-hidden">
                                    {attachment?.type.startsWith('image/') ? (
                                        <img src={attachmentPreview} alt="Vista previa" className="w-16 h-16 object-cover rounded-md" />
                                    ) : (
                                        <div className="w-16 h-16 bg-gray-700 flex items-center justify-center rounded-md text-gray-400">
                                            <Paperclip size={24} />
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-white truncate">{attachment?.name}</p>
                                        <p className="text-xs text-gray-400">Listo para enviar</p>
                                    </div>
                                </div>
                                <button 
                                    type="button" 
                                    onClick={handleRemoveAttachment}
                                    className="p-1 bg-gray-700 hover:bg-red-500 text-white rounded-full transition-colors"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        )}

                        {/* VISTA PREVIA DE CITA */}
                        <QuotedMessagePreview 
                            quotedMessage={quotedMessage}
                            onCancelQuote={() => setQuotedMessage(null)}
                            agentNames={agentNames}
                        />

                        <div className="flex items-start gap-2 w-full">
                            <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
                            
                            {/* Botón de Adjuntar: Deshabilitado si ya hay un adjunto */}
                            <button 
                                type="button" 
                                onClick={() => !attachment && fileInputRef.current.click()} 
                                className={`p-2 ${attachment ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 hover:text-hoprin-primary'}`}
                                disabled={!!attachment}
                            >
                                <Paperclip size={22}/>
                            </button>

                            <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="p-2 text-gray-400 hover:text-hoprin-primary"><Smile size={22}/></button>
                            
                            <textarea 
                                ref={inputRef} 
                                value={newMessage} 
                                onChange={handleInputChange} 
                                onPaste={handlePaste} 
                                onKeyDown={handleKeyDown} 
                                placeholder={attachment ? "Añade un comentario..." : "Escribe un mensaje..."} 
                                rows="1" 
                                className="flex-grow p-2 rounded-md bg-gray-900 border-transparent focus:border-hoprin-primary focus:ring-hoprin-primary text-sm resize-none overflow-y-auto max-h-40"
                            />
                            
                            <button type="submit" className="bg-hoprin-primary text-white p-3 rounded-full hover:opacity-90 shrink-0 self-end">
                                {isUploading ? (
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                ) : (
                                    <Send size={18}/>
                                )}
                            </button>
                        </div>
                    </form>
                </>
            ) : (
                <>
                    <div ref={internalNotesContainerRef} className="flex-grow overflow-y-auto bg-gray-900/50">
                        <InternalNoteList notes={internalNotes} agentNames={agentNames} />
                    </div>
                    {showMentionPopup && filteredMentionableAgents.length > 0 && (
                        <div ref={mentionPopupRef} className="absolute bottom-16 left-4 w-72 max-h-48 overflow-y-auto bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-20">
                            {filteredMentionableAgents.map(agent => (
                                <button key={agent.id} onClick={() => handleMentionSelect(agent)} className="w-full text-left p-2 hover:bg-gray-700">
                                    <p className="text-sm font-medium">{agent.name}</p>
                                </button>
                            ))}
                        </div>
                    )}
                    <form onSubmit={(e) => { e.preventDefault(); handleSendInternalNote(); }} className="bg-black p-2 flex items-start gap-2 border-t border-gray-700 shrink-0 relative">
                         <textarea
                            ref={noteTextareaRef}
                            value={newInternalNote}
                            onChange={handleInternalNoteChange}
                            onKeyDown={handleInternalNoteKeyDown}
                            placeholder="Escribe una nota interna... Usa @ para mencionar"
                            rows="2"
                            className="flex-grow p-2 rounded-md bg-gray-900 border-transparent focus:border-hoprin-primary focus:ring-hoprin-primary text-sm resize-none overflow-y-auto max-h-40"
                         />
                         <button type="submit" disabled={isSendingNote || showMentionPopup} className="bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 disabled:opacity-50 shrink-0 self-end">
                            <Send size={18}/>
                         </button>
                    </form>
                </>
            )}
        </div>
    );
};

const ChatsView = ({ allChats, selectedChat, onSelectChat, currentAgent, agents, isProfileVisible, onToggleProfile, onCloseChat, activeFilter, setActiveFilter, departmentFilter, setDepartmentFilter, departments, services, waSocket, tgSocket, quickReplies, allInternalNotes, onImageClick, initialChatTab }) => {
    const [mobileView, setMobileView] = useState('list');
    const [chatSearchTerm, setChatSearchTerm] = useState('');
    const [visibleCount, setVisibleCount] = useState(50);
    const listContainerRef = useRef(null);

    const agentDepartmentId = useMemo(() => {
        if (!currentAgent || !departments.length || !currentAgent.department) return null;
        const agentDept = departments.find(d => d.name === currentAgent.department);
        return agentDept ? agentDept.id : null;
    }, [currentAgent, departments]);

    const filteredAndSortedChats = useMemo(() => {
        return allChats
            .filter(chat => {
                const agentRole = currentAgent.role;
                if (agentRole === 'agente' || agentRole === 'supervisor') {
                    if (!chat.departmentIds || !chat.departmentIds.includes(agentDepartmentId)) {
                        return false;
                    }
                }
                if (departmentFilter !== 'todos' && (!chat.departmentIds || !chat.departmentIds.includes(departmentFilter))) {
                    return false;
                }
                switch (activeFilter) {
                    case 'para_mi': 
                        return chat.agentEmail === currentAgent.email && chat.status === 'Abierto';
                    case 'sin_asignar': 
                        return !chat.agentEmail && chat.status === 'Abierto';
                    case 'abierto': 
                        return chat.status === 'Abierto';
                    case 'cerrado':
                        return chat.status === 'Cerrado';
                    case 'con_bot':
                        return chat.isBotActive === true;
                    default: 
                        return true;
                }
            })
            .filter(chat => {
                if (!chatSearchTerm) return true;
                const lowerCaseSearch = chatSearchTerm.toLowerCase();
                return (
                    (chat.contactName || '').toLowerCase().includes(lowerCaseSearch) ||
                    (chat.contactPhone || '').includes(lowerCaseSearch) ||
                    (chat.internalId || '').toLowerCase().includes(lowerCaseSearch)
                );
            })
            .sort((a, b) => {
                if (a.status === 'Abierto' && b.status !== 'Abierto') return -1;
                if (a.status !== 'Abierto' && b.status === 'Abierto') return 1;
                return (b.lastMessageTimestamp?.toDate() || 0) - (a.lastMessageTimestamp?.toDate() || 0);
            });
    }, [allChats, activeFilter, departmentFilter, currentAgent, chatSearchTerm, agentDepartmentId]);    

    const displayedChats = useMemo(() => {
        return filteredAndSortedChats.slice(0, visibleCount);
    }, [filteredAndSortedChats, visibleCount]);

    useEffect(() => {
        setVisibleCount(50);
        if (listContainerRef.current) {
            listContainerRef.current.scrollTop = 0;
        }
    }, [activeFilter, chatSearchTerm, departmentFilter]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop < clientHeight + 400) {
            if (visibleCount < filteredAndSortedChats.length) {
                setVisibleCount(prevCount => prevCount + 50);
            }
        }
    };
    
    const handleSelectChat = (chat) => {
        onSelectChat(chat?.id);
        if (chat) {
            setMobileView('chat');
        }
    };

    return (
        <div className="flex h-full text-white overflow-hidden">
            <div className="md:hidden w-full h-full">
                {mobileView === 'list' && (
                     <ChatList 
                        chats={filteredAndSortedChats} // <-- CORREGIDO: Usar todos los chats filtrados para móvil
                        selectedChat={selectedChat} 
                        onSelectChat={handleSelectChat} 
                        agents={agents} 
                        activeFilter={activeFilter} 
                        setActiveFilter={setActiveFilter} 
                        departmentFilter={departmentFilter} 
                        setDepartmentFilter={setDepartmentFilter} 
                        currentAgent={currentAgent} 
                        departments={departments} 
                        searchTerm={chatSearchTerm} 
                        setSearchTerm={setChatSearchTerm}
                        scrollContainerRef={listContainerRef}
                        onScroll={handleScroll}
						allInternalNotes={allInternalNotes}
                        //currentAgent={currentAgent} // <-- ELIMINADO DUPLICADO
                    />
                )}
                {mobileView === 'chat' && selectedChat && (
                    <ChatWindow
                        key={selectedChat.id}
                        selectedChat={selectedChat}
                        agents={agents}
			            departments={departments}
                        onToggleProfile={() => setMobileView('profile')}
                        onCloseChat={onCloseChat}
                        waSocket={waSocket}
                        tgSocket={tgSocket}
                        currentAgent={currentAgent}
                        quickReplies={quickReplies}
                        onMobileBack={() => { onSelectChat(null); setMobileView('list'); }}
                        onImageClick={onImageClick}
                        initialChatTab={initialChatTab} // <-- AÑADIDO initialChatTab
                    />
                )}
                {mobileView === 'profile' && selectedChat && (
                    <ChatProfile 
                        selectedChat={selectedChat} 
                        services={services} 
                        currentAgent={currentAgent}
                        onMobileBack={() => setMobileView('chat')}
                        onToggleProfile={() => setMobileView('chat')}
                    />
                )}
            </div>

            <div className="hidden md:flex flex-1 overflow-hidden">
                <div className="w-full md:w-1/3 lg:w-1/4 shrink-0 border-r border-gray-700 flex flex-col">
                     <ChatList 
                        chats={displayedChats} // <-- Usa chats paginados para escritorio
                        selectedChat={selectedChat} 
                        onSelectChat={handleSelectChat} 
                        agents={agents} 
                        activeFilter={activeFilter} 
                        setActiveFilter={setActiveFilter} 
                        departmentFilter={departmentFilter} 
                        setDepartmentFilter={setDepartmentFilter} 
                        currentAgent={currentAgent} 
                        departments={departments} 
                        searchTerm={chatSearchTerm} 
                        setSearchTerm={setChatSearchTerm}
                        scrollContainerRef={listContainerRef}
                        onScroll={handleScroll}
						allInternalNotes={allInternalNotes}
                        //currentAgent={currentAgent} // <-- ELIMINADO DUPLICADO
                     />
                </div>
                <div className="flex-1 flex overflow-hidden">
                    {selectedChat ? (
                        <ChatWindow
                            key={selectedChat.id}
                            selectedChat={selectedChat}
                            agents={agents}
                            departments={departments}
                            onToggleProfile={onToggleProfile}
                            onCloseChat={onCloseChat}
							waSocket={waSocket}
							tgSocket={tgSocket}
                            currentAgent={currentAgent}
                            quickReplies={quickReplies}
                            onImageClick={onImageClick}
                            initialChatTab={initialChatTab} // <-- AÑADIDO initialChatTab
                        />
                    ) : (
                        <div className="bg-black flex-grow flex flex-col justify-center items-center text-gray-500 h-full">
                            <MessageSquareText size={64} className="mb-4"/>
                            <h2 className="text-xl font-semibold">Selecciona un chat</h2>
                            <p>Elige una conversación de la lista para empezar.</p>
                        </div>
                    )}
                    {isProfileVisible && selectedChat && (
                        <div className="w-full md:w-2/5 lg:w-1/3 shrink-0">
                             <ChatProfile selectedChat={selectedChat} onToggleProfile={onToggleProfile} services={services} currentAgent={currentAgent} onMobileBack={() => {}} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ContactListItem = ({ contact, isSelected, onSelect }) => (
    <button onClick={() => onSelect(contact)} className={`w-full text-left p-4 flex items-center gap-3 border-b border-gray-700 hover:bg-gray-900/50 ${isSelected ? 'bg-hoprin-accent/10' : ''}`}>
        <Avatar internalId={contact.internalId} size="md" />
        <div className="flex-grow overflow-hidden">
            <p className="font-semibold truncate">{contact.contactName}</p>
            <p className="text-sm text-gray-400">{contact.platform === 'telegram' ? (contact.telegramUsername ? `@${contact.telegramUsername}` : contact.contactId) : (contact.contactPhone ? contact.contactPhone.split('@')[0] : '')}</p>
        </div>
    </button>
);

const ContactList = ({ contacts, selected, onSelect, searchTerm, setSearchTerm, onImportClick, onNewChatClick, scrollContainerRef, onScroll }) => (
    <div className="bg-black border-r border-gray-700 flex flex-col h-full w-full">
        <div className="p-4 border-b border-gray-700 space-y-4 shrink-0">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Contactos ({contacts.length})</h2>
                <div className="flex items-center gap-2">
                    {onNewChatClick && (
                        <button onClick={onNewChatClick} className="flex items-center gap-2 bg-green-500 text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity text-sm" title="Iniciar nueva conversación">
                            <PlusCircle size={16}/>
                        </button>
                    )}
                    {onImportClick && (
                        <button onClick={onImportClick} className="flex items-center gap-2 bg-hoprin-primary text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity text-sm">
                            <UploadCloud size={16}/> Importar
                        </button>
                    )}
                </div>
            </div>
            <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar por nombre, teléfono o ID..."
                    className="w-full bg-gray-800 rounded-md p-2 pl-10 text-sm focus:ring-1 focus:ring-hoprin-primary focus:outline-none" />
            </div>
        </div>

        <div ref={scrollContainerRef} onScroll={onScroll} className="flex-grow overflow-y-auto gpu-layer">
            {contacts.map(contact => ( <ContactListItem key={contact.id} contact={contact} isSelected={selected?.id === contact.id} onSelect={onSelect} /> ))}
        </div>

    </div>
);

const NewChatModal = ({ onClose, onStartChat, channels, currentAgent, departments }) => {
    const [name, setName] = useState('');
    const [recipient, setRecipient] = useState('');
    const [message, setMessage] = useState('');
    const [selectedChannelId, setSelectedChannelId] = useState('');
    const [departmentId, setDepartmentId] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        
        const digitsOnly = recipient.replace(/\D/g, '');
        if (!name.trim() || !digitsOnly || !departmentId) {
            setError('El nombre, teléfono y departamento son obligatorios.');
            return;
        }
        
        onStartChat({
            name: name.trim(),
            recipientNumber: digitsOnly,
            channelId: selectedChannelId, // Puede ir vacío si no hay mensaje
            initialMessage: message,
            agentEmail: currentAgent.email,
            agentName: currentAgent.name,
            departmentId: departmentId,
        });
    };
    
    // --- CORRECCIÓN AQUÍ: Usar 'platform' en lugar de 'type' ---
    const availableChannels = channels.filter(c => c.platform === 'whatsapp' && c.connected === 'CONNECTED');
    // ----------------------------------------------------------

    return (
        <Modal onClose={onClose} size="lg">
            <h2 className="text-2xl font-bold text-center mb-6">
                {message.trim() ? "Iniciar Nueva Conversación" : "Crear Nuevo Contacto"}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Nombre del Contacto</label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre y Apellido" className="w-full p-2 border rounded-md bg-gray-700" required />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Número de Teléfono</label>
                    <input type="text" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Ej: 584121234567" className="w-full p-2 border rounded-md bg-gray-700" required />
                    <p className="text-xs text-gray-500 mt-1">Incluye el código de país (ej. 58 para Venezuela).</p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Asignar al Departamento</label>
                    <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className="w-full p-2 border rounded-md bg-gray-700" required>
                        <option value="" disabled>Selecciona un departamento...</option>
                        {departments.map(dept => <option key={dept.id} value={dept.id}>{dept.name}</option>)}
                    </select>
                </div>
                
                <div className="pt-4 border-t border-gray-600">
                    <p className="text-sm font-bold text-gray-300 mb-2">Opcional: Enviar mensaje inicial</p>
                    {availableChannels.length > 0 ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Enviar desde el Canal</label>
                            <select value={selectedChannelId} onChange={(e) => setSelectedChannelId(e.target.value)} className="w-full p-2 border rounded-md bg-gray-700">
                                <option value="" disabled>Selecciona un canal...</option>
                                {availableChannels.map(channel => ( <option key={channel.id} value={channel.id}>{channel.name}</option> ))}
                            </select>
                        </div>
                    ) : (
                         <p className="text-xs text-yellow-500 mb-2">No hay canales conectados para enviar mensaje inicial, pero puedes crear el contacto.</p>
                    )}
                    
                    <div className="mt-2">
                        <label className="block text-sm font-medium text-gray-300 mb-1">Mensaje</label>
                        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows="3" placeholder="Escribe tu primer mensaje aquí..." className="w-full p-2 border rounded-md bg-gray-700"></textarea>
                    </div>
                </div>

                {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                
                <button type="submit" className="w-full flex justify-center items-center gap-2 bg-green-500 text-white py-2 rounded-lg mt-4 hover:bg-green-600 transition-opacity">
                    {message.trim() && selectedChannelId ? <><Send size={18}/> Enviar e Iniciar Chat</> : 'Guardar Contacto'}
                </button>
            </form>
        </Modal>
    );
};

const ContactsView = ({ allChats, services, currentAgent, departments, onImportSuccess, onSelectChat, isProfileVisible, onToggleProfile, selectedChat, waSocket, channels, importContacts }) => {
    const [mobileView, setMobileView] = useState('list');
    const [searchTerm, setSearchTerm] = useState('');
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);

    const [visibleCount, setVisibleCount] = useState(50);
    const listContainerRef = useRef(null);

    const uniqueContacts = useMemo(() => {
        const contactsMap = new Map();
        allChats.forEach(chat => {
            const contactId = chat.contactPhone || chat.contactId;
            if (!contactId) return;
            const existing = contactsMap.get(contactId);
            if (!existing || (chat.lastMessageTimestamp && existing.lastMessageTimestamp && chat.lastMessageTimestamp > existing.lastMessageTimestamp)) {
                contactsMap.set(contactId, chat);
            }
        });
        return Array.from(contactsMap.values()).sort((a, b) => (a.contactName || '').localeCompare(b.contactName || ''));
    }, [allChats]);
    
    const filteredContacts = useMemo(() => {
        if (!searchTerm) return uniqueContacts;
        const lowerCaseSearch = searchTerm.toLowerCase();
        return uniqueContacts.filter(contact =>
            (contact.contactName || '').toLowerCase().includes(lowerCaseSearch) ||
            (contact.contactPhone || '').includes(lowerCaseSearch) ||
            (contact.contactId || '').includes(lowerCaseSearch) ||
            (contact.internalId || '').toLowerCase().includes(lowerCaseSearch)
        );
    }, [uniqueContacts, searchTerm]);

    const displayedContacts = useMemo(() => {
        return filteredContacts.slice(0, visibleCount);
    }, [filteredContacts, visibleCount]);

    useEffect(() => {
        setVisibleCount(50);
        if (listContainerRef.current) {
            listContainerRef.current.scrollTop = 0;
        }
    }, [searchTerm]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop < clientHeight + 400) {
            if (visibleCount < filteredContacts.length) {
                setVisibleCount(prevCount => prevCount + 50);
            }
        }
    };
    
    const handleSelectContact = (contact) => {
        onSelectChat(contact?.id);
    };

    const handleStartNewChat = (chatData) => {
        if (waSocket) { // <-- Usa waSocket
    waSocket.emit('iniciar_nuevo_chat', chatData); // <-- Usa waSocket
    waSocket.once('nuevo_chat_iniciado', ({ chatId }) => { // <-- Usa waSocket
        onSelectChat(chatId, true);
    });
}
setIsNewChatModalOpen(false);
    };
    
    const canStartNewChat = currentAgent.role === 'gerente' || currentAgent.role === 'administrador';
    
    return (
        <div className="flex h-full text-white overflow-hidden">
            {isImportModalOpen && <ImportContactsModal departments={departments} onClose={() => setIsImportModalOpen(false)} onSuccess={onImportSuccess} importContacts={importContacts} />}
            
            {/* --- INICIO DE LA CORRECCIÓN --- */}
            {isNewChatModalOpen && <NewChatModal 
                onClose={() => setIsNewChatModalOpen(false)} 
                onStartChat={handleStartNewChat} 
                channels={channels} 
                currentAgent={currentAgent} 
                departments={departments} // <-- ESTE PROP FALTABA
            />}
            {/* --- FIN DE LA CORRECCIÓN --- */}

             <div className="md:hidden w-full h-full">
                 {mobileView === 'list' && (
                     <ContactList 
                         contacts={displayedContacts} 
                         selected={selectedChat}
                         onSelect={(contact) => {
                             handleSelectContact(contact);
                             setMobileView('profile');
                         }}
                         searchTerm={searchTerm}
                         setSearchTerm={setSearchTerm}
                         onImportClick={() => setIsImportModalOpen(true)}
                         onNewChatClick={canStartNewChat ? () => setIsNewChatModalOpen(true) : null}
                         scrollContainerRef={listContainerRef}
                         onScroll={handleScroll}
                     />
                 )}
                 {mobileView === 'profile' && selectedChat && (
                     <ChatProfile 
                         selectedChat={selectedChat} 
                         services={services} 
                         currentAgent={currentAgent}
                         onMobileBack={() => { onSelectChat(null); setMobileView('list'); }}
                     />
                 )}
             </div>
             <div className="hidden md:flex flex-1 overflow-hidden h-full">
                <div className="w-full md:w-1/3 lg:w-1/4 h-full shrink-0 border-r border-gray-700">
                     <ContactList 
                         contacts={displayedContacts}
                         selected={selectedChat}
                         onSelect={handleSelectContact}
                         searchTerm={searchTerm}
                         setSearchTerm={setSearchTerm}
                         onImportClick={() => setIsImportModalOpen(true)}
                         onNewChatClick={canStartNewChat ? () => setIsNewChatModalOpen(true) : null}
                         scrollContainerRef={listContainerRef}
                         onScroll={handleScroll}
                     />
                </div>
                <div className="flex-1 flex overflow-hidden">
                    {isProfileVisible && selectedChat ? (
                        <ChatProfile 
                            selectedChat={selectedChat} 
                            onToggleProfile={onToggleProfile}
                            services={services} 
                            currentAgent={currentAgent} 
                         />
                    ) : (
                        <div className="m-auto text-center text-gray-500 flex flex-col items-center">
                            <UsersIcon size={64} className="text-gray-600 mb-4"/>
                            <h2 className="text-xl font-bold text-gray-300">Selecciona un contacto</h2>
                            <p>Aquí verás su información detallada.</p>
                        </div>
                    )}
                </div>
             </div>
        </div>
     );
};

const CalculatorModal = ({ onClose }) => {
    const [activeTab, setActiveTab] = useState('basic');
    const [usdtRate, setUsdtRate] = useState('');
    const [usdtAmount, setUsdtAmount] = useState('');
    const [bsAmount, setBsAmount] = useState(0);
    const [discountPrice, setDiscountPrice] = useState('');
    const [discount, setDiscount] = useState('');
    const [discountFinalPrice, setDiscountFinalPrice] = useState(0);
    const [basicInput, setBasicInput] = useState('');
    const [basicResult, setBasicResult] = useState(0);

    const evaluateExpression = (expr) => {
        try {
            const sanitizedExpr = expr.replace(/[^0-9+\-*/().]/g, '');
            // eslint-disable-next-line no-new-func
            return new Function('return ' + sanitizedExpr)();
        } catch (error) {
            return 'Error';
        }
    };

    useEffect(() => {
        if (basicInput) {
            const result = evaluateExpression(basicInput);
            setBasicResult(result);
        } else {
            setBasicResult(0);
        }
    }, [basicInput]);

    useEffect(() => {
        const rate = parseFloat(usdtRate) || 0;
        const amount = parseFloat(usdtAmount) || 0;
        setBsAmount((rate * amount).toFixed(2));
    }, [usdtRate, usdtAmount]);

    useEffect(() => {
        const price = parseFloat(discountPrice) || 0;
        const disc = parseFloat(discount) || 0;
        setDiscountFinalPrice((price * (1 - disc / 100)).toFixed(2));
    }, [discountPrice, discount]);

    const handleBasicButtonClick = (value) => {
        if (value === 'C') {
            setBasicInput('');
            setBasicResult(0);
        } else if (value === '=') {
            setBasicInput(basicResult.toString());
        } else {
            setBasicInput(prev => prev + value);
        }
    };
    
    const renderBasicCalculator = () => (
        <div className='space-y-4'>
            <div className='bg-black p-4 rounded-lg text-right'>
                 <p className="text-gray-400 text-lg break-all">{basicInput || '0'}</p>
                 <p className="text-white text-4xl font-bold">{basicResult}</p>
            </div>
            <div className='grid grid-cols-4 gap-2'>
                {['C', '/', '*', '7', '8', '9', '-', '4', '5', '6', '+', '1', '2', '3', '=', '0', '.'].map(btn => (
                     <button key={btn} onClick={() => handleBasicButtonClick(btn)} className={`p-4 rounded-lg text-xl font-bold ${btn === '=' ? 'col-span-2 bg-hoprin-primary' : 'bg-gray-700 hover:bg-gray-600'}`}>
                         {btn}
                     </button>
                ))}
            </div>
        </div>
    );

    const renderUsdtConverter = () => (
        <div className='space-y-4'>
            <div> <label className="block text-sm font-medium text-gray-300">Tasa USDT a Bs.</label> <input type="number" value={usdtRate} onChange={(e) => setUsdtRate(e.target.value)} placeholder="Ej: 38.5" className="mt-1 w-full p-2 border rounded-md bg-gray-700" /> </div>
            <div> <label className="block text-sm font-medium text-gray-300">Monto en USDT</label> <input type="number" value={usdtAmount} onChange={(e) => setUsdtAmount(e.target.value)} placeholder="0.00" className="mt-1 w-full p-2 border rounded-md bg-gray-700" /> </div>
            <div className="text-center bg-black p-4 rounded-lg"> <p className="text-sm font-medium text-gray-400">TOTAL EN BOLÍVARES</p> <p className="text-3xl font-bold text-hoprin-primary">{bsAmount} Bs.</p> </div>
        </div>
    );

    const renderDiscountCalculator = () => (
        <div className='space-y-4'>
            <div> <label className="block text-sm font-medium text-gray-300">Precio Estándar</label> <input type="number" value={discountPrice} onChange={(e) => setDiscountPrice(e.target.value)} placeholder="0.00" className="mt-1 w-full p-2 border rounded-md bg-gray-700" /> </div>
            <div> <label className="block text-sm font-medium text-gray-300">Descuento (%)</label> <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0" className="mt-1 w-full p-2 border rounded-md bg-gray-700" /> </div>
            <div className="text-center bg-black p-4 rounded-lg"> <p className="text-sm font-medium text-gray-400">PRECIO FINAL</p> <p className="text-3xl font-bold text-hoprin-primary">${discountFinalPrice}</p> </div>
        </div>
    );

    return (
        <Modal onClose={onClose} size="md">
            <h2 className="text-2xl font-bold text-center mb-4">Calculadora</h2>
            <div className='flex justify-center border-b border-gray-700 mb-4'>
                <button onClick={() => setActiveTab('basic')} className={`px-4 py-2 text-sm ${activeTab === 'basic' ? 'border-b-2 border-hoprin-primary text-hoprin-primary' : ''}`}>Básica</button>
                <button onClick={() => setActiveTab('usdt')} className={`px-4 py-2 text-sm ${activeTab === 'usdt' ? 'border-b-2 border-hoprin-primary text-hoprin-primary' : ''}`}>Conversor USDT</button>
                <button onClick={() => setActiveTab('discount')} className={`px-4 py-2 text-sm ${activeTab === 'discount' ? 'border-b-2 border-hoprin-primary text-hoprin-primary' : ''}`}>Descuentos</button>
            </div>
            {activeTab === 'basic' && renderBasicCalculator()}
            {activeTab === 'usdt' && renderUsdtConverter()}
            {activeTab === 'discount' && renderDiscountCalculator()}
        </Modal>
    );
};



const ReportsView = ({ departments, generatePerformanceReport }) => {
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const todayISO = today.toISOString().split('T')[0];

    const [startDate, setStartDate] = useState(firstDayOfMonth);
    const [endDate, setEndDate] = useState(todayISO);
    const [departmentFilter, setDepartmentFilter] = useState('todos');

    const handleGenerateReport = useCallback(async () => {
        setIsLoading(true);
        setError('');
        setReportData(null);

        if (!startDate || !endDate) {
            setError("Por favor, selecciona un rango de fechas válido.");
            setIsLoading(false);
            return;
        }

        try {

            const result = await generatePerformanceReport({
                startDate,
                endDate,
				
                departmentId: departmentFilter === 'todos' ? null : departmentFilter
            });

            if (result.data.report) {
                setReportData(result.data.report);
            } else {
                throw new Error("La respuesta del servidor no contiene un reporte válido.");
            }
        } catch (err) {
            console.error("Error generando el reporte (onCall):", err);
            setError(`Error al generar el reporte: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, departmentFilter]);

    return(
        <div className="p-4 md:p-8 text-white h-full overflow-y-auto">
            <h1 className="text-3xl font-bold mb-6">Reporte de Productividad por Agente</h1>
            
            <div className="bg-gray-800 p-4 rounded-lg shadow-md flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4 flex-wrap">
                     <div className="flex items-center gap-2">
                        <Calendar size={18}/>
                        <label>Desde:</label>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-gray-700 p-2 rounded-md text-sm"/>
                     </div>
                     <div className="flex items-center gap-2">
                        <label>Hasta:</label>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-gray-700 p-2 rounded-md text-sm"/>
                     </div>
                     <div className="flex items-center gap-2">
                        <Briefcase size={18}/>
                        <label>Departamento:</label>
                        <select value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)} className="bg-gray-700 p-2 rounded-md text-sm">
                            <option value="todos">Todos los Departamentos</option>
                            {departments.map(dept => <option key={dept.id} value={dept.name}>{dept.name}</option>)}
                        </select>
                     </div>
                </div>
                <button 
                    onClick={handleGenerateReport} 
                    disabled={isLoading}
                    className="flex items-center gap-2 bg-hoprin-primary text-white px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
                >
                    <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                    {isLoading ? 'Generando...' : 'Generar Reporte'}
                </button>
            </div>

            {error && <p className="text-center text-red-400 bg-red-500/10 py-2 rounded-md">{error}</p>}
            
            {isLoading && (
                <div className="text-center p-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-hoprin-primary mx-auto mb-4"></div>
                    <p>Calculando métricas, por favor espera...</p>
                </div>
            )}

            {reportData && (
                <div className="bg-gray-800 p-4 md:p-6 rounded-lg shadow-md">
                    <div className="flex flex-col md:flex-row justify-between md:items-center mb-4">
                        <h2 className="text-xl font-semibold">Resultados del Reporte</h2>
                        <p className="text-sm text-gray-400 mt-2 md:mt-0">
                            Mostrando datos desde {new Date(reportData.startDate).toLocaleDateString('es-VE')} hasta {new Date(reportData.endDate).toLocaleDateString('es-VE')}
                        </p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="border-b-2 border-gray-700">
                                <tr>
                                    <th className="p-3">Agente</th>
                                    <th className="p-3 text-center">Chats Cerrados</th>
                                    <th className="p-3">T. Prom. 1ra Respuesta</th>
                                    <th className="p-3">T. Prom. de Cierre</th>
                                    <th className="p-3 text-center">Calificación Prom.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.agentPerformance.length > 0 ? (
                                    reportData.agentPerformance.map(row => (
                                        <tr key={row.agentEmail} className="border-b border-gray-700 hover:bg-gray-700/50">
                                            <td className="p-3 font-semibold">{row.agentName}</td>
                                            <td className="p-3 text-center">{row.closedChats}</td>
                                            <td className="p-3">{row.avgFirstResponseTime}</td>
                                            <td className="p-3">{row.avgClosingTime}</td>
                                            <td className="p-3 text-center">{row.avgRating} ⭐</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="p-4 text-center text-gray-500">No se encontraron datos para estos filtros.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};




const UserForm = ({ isEditing, onSave, onCancel, formData, setFormData, isLoading }) => {
    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    return (
        <Modal onClose={onCancel}>
            <h2 className="text-2xl font-bold text-center mb-6">{isEditing ? 'Editar Usuario' : 'Crear Nuevo Usuario'}</h2>
            <form onSubmit={(e) => { e.preventDefault(); onSave(); }} className="space-y-4">
                <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Nombre Completo" className="w-full p-2 border rounded-md bg-gray-700" required />
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email" className="w-full p-2 border rounded-md bg-gray-700" required disabled={isEditing} />
                {!isEditing && (
                    <input type="password" name="password" value={formData.password} onChange={handleChange} placeholder="Contraseña (mín. 6 caracteres)" className="w-full p-2 border rounded-md bg-gray-700" required />
                )}
                <select name="department" value={formData.department} onChange={handleChange} className="w-full p-2 border rounded-md bg-gray-700"><option>Atención al Cliente</option><option>Gestión de Cuentas</option><option>Contabilidad</option><option>Gerencia</option></select>
                <select name="role" value={formData.role} onChange={handleChange} className="w-full p-2 border rounded-md bg-gray-700"> <option value="agente">Agente</option> <option value="supervisor">Supervisor</option> <option value="gerente">Gerente</option> <option value="administrador">Administrador</option> </select>
                <button type="submit" className="w-full bg-hoprin-primary text-white py-2 rounded-lg mt-4 hover:opacity-90 transition-opacity" disabled={isLoading}>
                    {isLoading ? 'Guardando...' : 'Guardar'}
                </button>
            </form>
        </Modal>
    );
};

const PasswordChangeModal = ({ agent, onSave, onCancel }) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (newPassword.length < 6) {
            setError("La contraseña debe tener al menos 6 caracteres.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setError("Las contraseñas no coinciden.");
            return;
        }
        onSave(agent.uid, newPassword);
    };

    return (
        <Modal onClose={onCancel} size="sm">
            <h2 className="text-2xl font-bold text-center mb-4">Cambiar Contraseña</h2>
            <p className="text-center text-gray-300 mb-6">Estás cambiando la contraseña para <span className="font-bold">{agent.name}</span>.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
                <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => { setError(''); setNewPassword(e.target.value); }}
                    placeholder="Nueva Contraseña"
                    className="w-full p-2 border rounded-md bg-gray-700"
                    required
                />
                <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => { setError(''); setConfirmPassword(e.target.value); }}
                    placeholder="Confirmar Nueva Contraseña"
                    className="w-full p-2 border rounded-md bg-gray-700"
                    required
                />
                {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                <button type="submit" className="w-full bg-hoprin-primary text-white py-2 rounded-lg mt-4 hover:opacity-90 transition-opacity">
                    Actualizar Contraseña
                </button>
            </form>
        </Modal>
    );
};

const UserManagementPanel = ({ agents, onConfirmDelete, currentAgent, createAgent, changeAgentPassword }) => {
    const [showUserModal, setShowUserModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null); 
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({ name: '', email: '', role: 'agente', department: 'Atención al Cliente', password: '' });

    const handleSaveUser = async () => {
        setIsLoading(true);
        try {
            if (editingUser) {
                const { id, uid, ...userData } = formData;
                const userRef = doc(db, 'agents', uid); 
                await updateDoc(userRef, userData);
                alert("Agente actualizado con éxito.");
            } else {
                const result = await createAgent(formData);
                alert(result.data.result);
            }
        } catch (error) {
            console.error("Error en la operación del agente:", error);
            alert(`Error: ${error.message}`);
        }
        setIsLoading(false);
        setShowUserModal(false);
        setEditingUser(null);
    };

    const handleChangePassword = async (uid, newPassword) => {
        setIsLoading(true);
        try {
            const result = await changeAgentPassword({ uid, newPassword });
            alert(result.data.result);
            setShowPasswordModal(false);
            setEditingUser(null);
        } catch (error) {
            console.error("Error al cambiar contraseña:", error);
            alert(`Error: ${error.message}`);
        }
        setIsLoading(false);
    };

    // --- ¡CORRECCIÓN! ---
    // El 'UserForm' ya no se define aquí.
    // --- FIN CORRECCIÓN ---

    const handleOpenCreateModal = () => {
        setEditingUser(null);
        setFormData({ name: '', email: '', role: 'agente', department: 'Atención al Cliente', password: '' });
        setShowUserModal(true);
    };

    const handleOpenEditModal = (agent) => {
        setEditingUser(agent);
        setFormData(agent);
        setShowUserModal(true);
    };

    return (
        <div className="bg-gray-800 p-4 md:p-6 rounded-lg shadow-md">
            {isLoading && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-hoprin-primary"></div></div>}

            {showUserModal && (
                <UserForm 
                    isEditing={!!editingUser} 
                    onSave={handleSaveUser} 
                    onCancel={() => { setShowUserModal(false); setEditingUser(null); }} 
                    formData={formData}
                    setFormData={setFormData}
                    isLoading={isLoading}
                />
            )}
            {/* --- FIN CORRECCIÓN --- */}

            {showPasswordModal && <PasswordChangeModal agent={editingUser} onSave={handleChangePassword} onCancel={() => { setShowPasswordModal(false); setEditingUser(null); }} />}
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Usuarios del Sistema</h2>
                {currentAgent.role === 'administrador' && (
                    <button onClick={handleOpenCreateModal} className="flex items-center gap-2 bg-hoprin-primary text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity text-sm"><PlusCircle size={18}/> Crear Usuario</button>
                )}
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="border-b-2 border-gray-700"><tr><th className="p-3">Nombre</th><th className="p-3">Email</th><th className="p-3">Rol</th><th className="p-3">Acciones</th></tr></thead>
                    <tbody>
                        {agents.map(agent => (
                            <tr key={agent.id} className="border-b border-gray-700">
                                <td className="p-3">{agent.name}</td>
                                <td className="p-3">{agent.email}</td>
                                <td className="p-3">{agent.role}</td>
                                <td className="p-3 flex gap-4">
                                    <button onClick={() => handleOpenEditModal(agent)} className="text-hoprin-primary hover:underline">Editar</button>
                                    <button onClick={() => { setEditingUser(agent); setShowPasswordModal(true); }} className="text-gray-400 hover:underline">Clave</button>
                                    <button onClick={() => onConfirmDelete('agent', { uid: agent.uid, docId: agent.id })} className="text-red-500 hover:underline">Eliminar</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const QuickReplyPanel = ({ replies, onConfirmDelete }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingReply, setEditingReply] = useState(null);
    const [expandedReplyId, setExpandedReplyId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState(''); // <-- 1. Añadimos estado para la búsqueda
    const repliesPerPage = 10;

    const handleSave = async (reply) => {
        const { id, ...replyData } = reply;
        if (editingReply) {
            await updateDoc(doc(db, 'quickReplies', id), replyData);
        } else {
            await addDoc(collection(db, 'quickReplies'), { ...replyData, createdAt: serverTimestamp() });
        }
        setShowModal(false);
        setEditingReply(null);
    };

    // ---
    // --- 2. Añadimos lógica de filtrado ---
    // ---
    const filteredReplies = useMemo(() => {
        if (!searchTerm) return replies;
        const lower = searchTerm.toLowerCase();
        return replies.filter(reply =>
            reply.title.toLowerCase().includes(lower) ||
            reply.text.toLowerCase().includes(lower) ||
            (reply.department && reply.department.toLowerCase().includes(lower))
        );
    }, [replies, searchTerm]);
    // ---
    // --- Fin de la lógica de filtrado ---
    // ---

    // La paginación ahora usa las respuestas filtradas
    const totalPages = Math.ceil(filteredReplies.length / repliesPerPage);
    const indexOfLastReply = currentPage * repliesPerPage;
    const indexOfFirstReply = indexOfLastReply - repliesPerPage;
    const currentReplies = filteredReplies.slice(indexOfFirstReply, indexOfLastReply);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const ReplyForm = ({ reply, onSave, onCancel }) => { 
        const [formData, setFormData] = useState(reply || { title: '', text: '', department: 'Atención al Cliente' });
        const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
        return ( <Modal onClose={onCancel}> <h2 className="text-2xl font-bold text-center mb-6">{reply ? 'Editar Respuesta' : 'Crear Respuesta Rápida'}</h2> <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="space-y-4"> <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="Título (ej: Saludo)" className="w-full p-2 border rounded-md bg-gray-700" required /> <textarea name="text" value={formData.text} onChange={handleChange} placeholder="Texto de la respuesta..." className="w-full p-2 border rounded-md bg-gray-700" rows="4" required></textarea> <select name="department" value={formData.department} onChange={handleChange} className="w-full p-2 border rounded-md bg-gray-700"><option>Atención al Cliente</option><option>Gestión de Cuentas</option><option>Contabilidad</option></select> <button type="submit" className="w-full bg-hoprin-primary text-white py-2 rounded-lg mt-4 hover:opacity-90 transition-opacity">Guardar</button> </form> </Modal> );
    };

    // Esta lógica ahora usa 'currentReplies' (que ya están filtradas y paginadas)
    const repliesByDept = currentReplies.reduce((acc, reply) => { 
        (acc[reply.department] = acc[reply.department] || []).push(reply); 
        return acc; 
    }, {});

    return ( 
        <div className="bg-gray-800 p-4 md:p-6 rounded-lg shadow-md"> 
            {showModal && <ReplyForm reply={editingReply} onSave={handleSave} onCancel={() => { setShowModal(false); setEditingReply(null); }} />} 
            
            <div className="flex justify-between items-center mb-6"> 
                <h2 className="text-xl font-semibold">Respuestas Rápidas</h2> 
                <button onClick={() => { setEditingReply(null); setShowModal(true); }} className="flex items-center gap-2 bg-hoprin-primary text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity text-sm"><PlusCircle size={18}/> Crear Respuesta</button> 
            </div> 

            {/* --- 3. Añadimos la barra de búsqueda --- */}
            <div className="relative mb-4">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar por título, texto o departamento..."
                    className="w-full bg-gray-700 rounded-md p-2 pl-10 text-sm focus:ring-1 focus:ring-hoprin-primary focus:outline-none"
                />
            </div>
            {/* --- Fin de la barra de búsqueda --- */}
            
            <div className="space-y-6"> 
                {Object.keys(repliesByDept).length > 0 ? Object.keys(repliesByDept).map(dept => ( 
                    <div key={dept}> 
                        <h3 className="font-bold text-lg mb-2 border-b-2 border-hoprin-accent pb-1">{dept}</h3> 
                        <div className="space-y-2"> 
                            {repliesByDept[dept].map(reply => (
                                <div key={reply.id} className="bg-gray-700 rounded-md overflow-hidden">
                                    <button onClick={() => setExpandedReplyId(expandedReplyId === reply.id ? null : reply.id)} className="w-full flex justify-between items-center p-3 text-left">
                                        <p className="font-semibold">{reply.title}</p>
                                        <div className="flex items-center gap-2">
                                            <button onClick={(e) => { e.stopPropagation(); setEditingReply(reply); setShowModal(true); }}><Pencil size={16} className="text-gray-500 hover:text-hoprin-primary"/></button>
                                            <button onClick={(e) => { e.stopPropagation(); onConfirmDelete('quickReply', reply.id)}}><Trash size={16} className="text-gray-500 hover:text-red-500"/></button>
                                            <ChevronDown className={`transition-transform ${expandedReplyId === reply.id ? 'rotate-180' : ''}`} />
                                        </div>
                                    </button>
                                    {expandedReplyId === reply.id && (
                                        <div className="p-3 border-t border-gray-600 bg-black/20">
                                            <p className="text-sm text-gray-300 whitespace-pre-wrap">{reply.text}</p>
                                        </div>
                                    )}
                                </div>
                            ))} 
                        </div> 
                    </div> 
                )) : (
                    <p className="text-center text-gray-500 py-4">
                        {searchTerm ? 'No se encontraron respuestas' : 'No hay respuestas rápidas creadas.'}
                    </p>
                )} 
            </div>
            {totalPages > 1 && (
                <div className="mt-6 flex justify-center items-center gap-2">
                    {Array.from({ length: totalPages }, (_, i) => (
                        <button key={i + 1} onClick={() => paginate(i + 1)} className={`px-3 py-1 rounded-md text-sm ${currentPage === i + 1 ? 'bg-hoprin-primary text-white' : 'bg-gray-700'}`}>
                            {i + 1}
                        </button>
                    ))}
                </div>
            )}
        </div> 
    );
};

const ServicesPanel = ({ services, onConfirmDelete }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingService, setEditingService] = useState(null);

    // --- INICIO DE LA CORRECCIÓN ---
    // Convertimos handleSave en una función asíncrona y añadimos la lógica de Firestore.
    const handleSave = async (service) => {
        const serviceData = { name: service.name };
        
        try {
            if (editingService) {
                // Lógica para ACTUALIZAR un servicio existente
                const serviceRef = doc(db, 'services', editingService.id);
                await updateDoc(serviceRef, serviceData);
            } else {
                // Lógica para CREAR un nuevo servicio
                await addDoc(collection(db, 'services'), serviceData);
            }
        } catch (error) {
            console.error("Error al guardar el servicio:", error);
            alert("No se pudo guardar el servicio.");
        }

        setShowModal(false);
        setEditingService(null);
    };
    // --- FIN DE LA CORRECCIÓN ---

    const ServiceForm = ({ service, onSave, onCancel }) => {
        const [name, setName] = useState(service ? service.name : '');
        return (
            <Modal onClose={onCancel} size="sm">
                <h2 className="text-2xl font-bold text-center mb-6">{service ? 'Editar Servicio' : 'Crear Nuevo Servicio'}</h2>
                <form onSubmit={(e) => { e.preventDefault(); onSave({ name }); }} className="space-y-4">
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre del Servicio" className="w-full p-2 border rounded-md bg-gray-700" required />
                    <button type="submit" className="w-full bg-hoprin-primary text-white py-2 rounded-lg mt-4 hover:opacity-90 transition-opacity">Guardar</button>
                </form>
            </Modal>
        );
    };

    return (
        <div className="bg-gray-800 p-4 md:p-6 rounded-lg shadow-md">
            {showModal && <ServiceForm service={editingService} onSave={handleSave} onCancel={() => { setShowModal(false); setEditingService(null); }} />}
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Gestión de Servicios</h2>
                <button onClick={() => { setEditingService(null); setShowModal(true); }} className="flex items-center gap-2 bg-hoprin-primary text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity text-sm"><PlusCircle size={18}/> Crear Servicio</button>
            </div>
            <div className="space-y-2">
                {services.map(service => (
                    <div key={service.id} className="bg-gray-700 p-3 rounded-md flex justify-between items-center">
                        <p>{service.name}</p>
                        <div className="flex gap-2">
                            <button onClick={() => { setEditingService(service); setShowModal(true); }}><Pencil size={16} className="text-gray-500 hover:text-hoprin-primary"/></button>
                            <button onClick={() => onConfirmDelete('service', service.id)}><Trash size={16} className="text-gray-500 hover:text-red-500"/></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ChannelsPanel = ({ channels, setChannels, currentAgent, departments, socket }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingChannel, setEditingChannel] = useState(null);
    const [newChannelName, setNewChannelName] = useState('');
    const [linkingDept, setLinkingDept] = useState({});

    const [telegramBotId, setTelegramBotId] = useState(null);
    const [telegramToken, setTelegramToken] = useState('');
    const [isTokenSaving, setIsTokenSaving] = useState(false);
    const [showToken, setShowToken] = useState(false); 

    useEffect(() => {
        const telegramBot = channels.find(c => c.platform === 'telegram' || c.type === 'telegram');
        if (telegramBot) {
            setTelegramBotId(telegramBot.id);
            setTelegramToken(telegramBot.botToken || ''); 
        }
    }, [channels]); 

    // Lógica de Ordenamiento
    const sortedChannels = useMemo(() => {
        return [...channels].sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            
            const getType = (ch) => ch.platform || ch.type;

            const getPriority = (channel, name) => {
                const type = getType(channel);
                if (type === 'whatsapp' && name.includes('atenci')) return 1;
                if (type === 'whatsapp' && (name.includes('gestor') || name.includes('cuentas'))) return 2;
                if (type === 'telegram') return 3;
                return 4;
            };

            return getPriority(a, nameA) - getPriority(b, nameB);
        });
    }, [channels]);

    const handleSyncStatus = () => {
        if (socket) {
            socket.emit('get_all_channel_statuses');
            const btn = document.getElementById('sync-btn');
            if(btn) btn.classList.add('animate-spin');
            setTimeout(() => { if(btn) btn.classList.remove('animate-spin'); }, 1000);
        }
    };

    const handleEditClick = (channel) => {
        setEditingChannel(channel);
        setNewChannelName(channel.name);
        setShowEditModal(true);
    };

    const handleSaveName = async () => {
        if (!editingChannel || !newChannelName.trim()) return;
        const channelRef = doc(db, 'channels', editingChannel.id);
        await updateDoc(channelRef, { name: newChannelName.trim() });
        setShowEditModal(false);
        setEditingChannel(null);
    };

    const handleLinkDepartment = (channelId) => {
        const departmentId = linkingDept[channelId];
        if (socket && departmentId) {
            socket.emit('link_channel_to_department', { channelId, departmentId });
        }
    };

    const handleSaveTelegramToken = async () => {
        if (!telegramBotId) return;
        setIsTokenSaving(true);
        try {
            const channelRef = doc(db, 'channels', telegramBotId);
            await updateDoc(channelRef, { botToken: telegramToken.trim() });
            alert("Token de Telegram guardado. El backend se reiniciará para aplicarlo.");
        } catch (error) {
            alert("Error al guardar el token.");
        }
        setIsTokenSaving(false);
    };

    return (
        <div className="bg-gray-800 p-4 md:p-6 rounded-lg shadow-md">
            {showEditModal && (
                <Modal onClose={() => setShowEditModal(false)} size="sm">
                    <h2 className="text-2xl font-bold mb-4">Editar Nombre</h2>
                    <input type="text" value={newChannelName} onChange={(e) => setNewChannelName(e.target.value)} className="w-full p-2 border rounded-md bg-gray-700" />
                    <button onClick={handleSaveName} className="w-full bg-hoprin-primary text-white py-2 rounded-lg mt-4 hover:opacity-90">Guardar</button>
                </Modal>
            )}

            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Gestión de Canales</h2>
                <button onClick={handleSyncStatus} className="flex items-center gap-2 bg-blue-600/20 text-blue-400 px-3 py-2 rounded-lg hover:bg-blue-600/40 transition-colors text-sm font-medium border border-blue-500/30">
                    <RefreshCw size={16} id="sync-btn" /> Sincronizar Estados
                </button>
            </div>

            <div className="space-y-4">
                {sortedChannels.map(channel => {
                    const isTelegram = channel.platform === 'telegram' || channel.type === 'telegram';
                    const isWhatsapp = channel.platform === 'whatsapp' || channel.type === 'whatsapp';
                    const isConnected = isTelegram ? !!channel.botToken && channel.botToken.length > 10 : channel.connected === 'CONNECTED';
                    
                    return (
                        <div key={channel.id} className="bg-gray-700 p-4 rounded-lg border border-gray-600 relative overflow-hidden">
                            <div className={`absolute left-0 top-0 bottom-0 w-1 ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <div className="flex flex-col md:flex-row md:justify-between md:items-center pl-2">
                                <div className="flex items-center gap-4">
                                    <div className="bg-black p-3 rounded-full shrink-0">
                                        {isTelegram ? <Send size={24} className="text-blue-400" /> : <Smartphone size={24} className="text-green-400" />}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="font-bold text-lg">{channel.name}</p>
                                            {currentAgent.role === 'administrador' && <button onClick={() => handleEditClick(channel)} className="text-gray-400 hover:text-white p-1"><Pencil size={14} /></button>}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                                            <p className={`text-sm font-semibold ${isConnected ? 'text-green-400' : 'text-red-400'}`}>{isConnected ? (isWhatsapp ? 'CONECTADO (Z-API)' : 'CONECTADO (BOT)') : 'DESCONECTADO'}</p>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-0.5">{isWhatsapp ? 'Gestionado vía Z-API Cloud' : (channel.statusMessage || 'Bot de Telegram')}</p>
                                    </div>
                                </div>
                                <div className="mt-4 md:mt-0">
                                     {(isConnected || isTelegram) && (
                                        <div className="flex items-center gap-2 bg-gray-800/50 p-2 rounded-md">
                                            {channel.departmentId ? (
                                                <p className="text-sm text-gray-300 flex items-center gap-2 px-2"><LinkIcon size={14} className="text-hoprin-primary"/> <span className="text-xs uppercase tracking-wide text-gray-500">Depto:</span><span className="font-semibold text-white">{departments.find(d => d.id === channel.departmentId)?.name || 'N/A'}</span></p>
                                            ) : (
                                                <>
                                                    <select onChange={(e) => setLinkingDept(prev => ({ ...prev, [channel.id]: e.target.value }))} defaultValue="" className="p-1.5 text-sm rounded-md bg-gray-900 border-gray-600 focus:ring-hoprin-primary text-white">
                                                        <option value="" disabled>Asignar departamento...</option>
                                                        {departments.map(dept => (<option key={dept.id} value={dept.id}>{dept.name}</option>))}
                                                    </select>
                                                    <button onClick={() => handleLinkDepartment(channel.id)} className="px-3 py-1.5 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white">Vincular</button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {isTelegram && currentAgent.role === 'administrador' && (
                                <div className="mt-4 pt-4 border-t border-gray-600/50">
                                    <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">Configuración del Bot</label>
                                    <div className="flex items-center gap-2">
                                        <div className="relative flex-grow">
                                            <input type={showToken ? "text" : "password"} placeholder="Token de BotFather..." value={telegramToken} onChange={(e) => setTelegramToken(e.target.value)} className="w-full p-2 pr-10 text-sm border border-gray-600 rounded-md bg-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all" />
                                            <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white" title={showToken ? "Ocultar token" : "Mostrar token"}>{showToken ? <EyeOff size={16}/> : <Eye size={16}/>}</button>
                                        </div>
                                        <button onClick={handleSaveTelegramToken} disabled={isTokenSaving} className="px-4 py-2 text-sm rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0">{isTokenSaving ? 'Guardando...' : 'Actualizar Token'}</button>
                                    </div>
                                </div>
                            )}
                            {isWhatsapp && !isConnected && (
                                <div className="mt-3 pt-3 border-t border-gray-600/50 text-center"><p className="text-xs text-yellow-500 flex items-center justify-center gap-2"><Info size={14}/> Este canal está desconectado. Por favor, escanea el QR en el panel de Z-API.</p></div>
                            )}
                        </div>
                    );
                })}
                {channels.length === 0 && <p className="text-center text-gray-500 py-8">No hay canales configurados en el sistema.</p>}
            </div>
        </div>
    );
};

const BotSettingsPanel = () => {
    const [settings, setSettings] = useState({
        isEnabled: true,
        welcomeEnabled: false,
        welcomeMessage: '¡Hola! 👋 Gracias por contactar a Hoprin+. Has sido asignado a un asesor de nuestro equipo y te atenderá en unos minutos. ¡Agradecemos tu paciencia!',
        closingEnabled: false,
        closingDelay: '10', // in minutes
        closingMessage: '¿Gracias por conversar con el equipo de Hoprin+! Esperamos haber resuelto tu consulta. 🙏\n\nPara ayudarnos a mejorar, ¿podrías calificar el soporte que recibiste?\n\nPor favor, solo reacciona a este mensaje.',
        awayEnabled: false,
        awayMessage: '¡Hola! 👋 Gracias por escribirnos. Nuestro horario de atención ha finalizado por hoy. Te responderemos tan pronto como nuestro equipo esté de vuelta. ¡Gracias por tu comprensión!',
        schedule: []
    });
    const [isLoading, setIsLoading] = useState(true);
    const settingsRef = useMemo(() => doc(db, 'settings', 'bot'), []);

    useEffect(() => {
        const fetchSettings = async () => {
            const docSnap = await getDoc(settingsRef);
            if (docSnap.exists()) {
                setSettings(prev => ({ ...prev, ...docSnap.data() }));
            }
            setIsLoading(false);
        };
        fetchSettings();
    }, [settingsRef]);

    const handleSettingChange = (field, value) => {
        const newSettings = { ...settings, [field]: value };
        setSettings(newSettings);
        setDoc(settingsRef, { [field]: value }, { merge: true });
    };
    
    const handleScheduleChange = (index, field, value) => {
        const newSchedule = [...settings.schedule];
        newSchedule[index][field] = value;
        handleSettingChange('schedule', newSchedule);
    };

    const addSchedule = () => {
        const newSchedule = [...settings.schedule, { day: 'Lunes a Viernes', start: '09:00', end: '17:00' }];
        handleSettingChange('schedule', newSchedule);
    };

    const removeSchedule = (index) => {
        const newSchedule = settings.schedule.filter((_, i) => i !== index);
        handleSettingChange('schedule', newSchedule);
    };
    
    const timeOptions = useMemo(() => {
        const options = [];
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m += 30) {
                const hour = h.toString().padStart(2, '0');
                const minute = m.toString().padStart(2, '0');
                options.push(`${hour}:${minute}`);
            }
        }
        return options;
    }, []);

    if (isLoading) return <div className="text-center p-10">Cargando configuración...</div>;

    const SettingCard = ({ title, description, enabled, onToggle, children }) => (
        <div className="bg-gray-700 p-4 rounded-lg">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="font-bold">{title}</h3>
                    <p className="text-sm text-gray-400">{description}</p>
                </div>
                 <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-hoprin-primary"></div>
                </label>
            </div>
            {enabled && <div className="mt-4 pt-4 border-t border-gray-600">{children}</div>}
        </div>
    );
    
    return (
        <div className="bg-gray-800 p-4 md:p-6 rounded-lg shadow-md space-y-6">
            <SettingCard
                title="Habilitar mensaje de bienvenida"
                description="Enviar una respuesta automática a cada nuevo chat."
                enabled={settings.welcomeEnabled}
                onToggle={(value) => handleSettingChange('welcomeEnabled', value)}
            >
                <textarea 
                    value={settings.welcomeMessage}
                    onChange={(e) => handleSettingChange('welcomeMessage', e.target.value)}
                    rows="4"
                    className="w-full p-2 text-sm rounded-md bg-gray-800 border-transparent focus:ring-hoprin-primary"
                ></textarea>
            </SettingCard>

             <SettingCard
                title="Habilitar mensaje de cierre"
                description="Enviar un mensaje personalizado después de cerrar un chat."
                enabled={settings.closingEnabled}
                onToggle={(value) => handleSettingChange('closingEnabled', value)}
            >
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <label className="text-sm">Enviar mensaje después de:</label>
                        <select 
                            value={settings.closingDelay} 
                            onChange={(e) => handleSettingChange('closingDelay', e.target.value)}
                            className="p-1 text-sm rounded-md bg-gray-800 border-transparent focus:ring-hoprin-primary"
                        >
                            <option value="1">1 minuto</option>
                            <option value="5">5 minutos</option>
                            <option value="10">10 minutos</option>
                            <option value="15">15 minutos</option>
                        </select>
                    </div>
                    <textarea 
                        value={settings.closingMessage}
                        onChange={(e) => handleSettingChange('closingMessage', e.target.value)}
                        rows="4"
                        className="w-full p-2 text-sm rounded-md bg-gray-800 border-transparent focus:ring-hoprin-primary"
                    ></textarea>
                </div>
            </SettingCard>
            
            <SettingCard
                title="Habilitar mensaje automático ausente"
                description="Si está habilitado, se enviará un mensaje fuera del horario laboral."
                enabled={settings.awayEnabled}
                onToggle={(value) => handleSettingChange('awayEnabled', value)}
            >
                <textarea 
                    value={settings.awayMessage}
                    onChange={(e) => handleSettingChange('awayMessage', e.target.value)}
                    rows="4"
                    className="w-full p-2 text-sm rounded-md bg-gray-800 border-transparent focus:ring-hoprin-primary"
                ></textarea>
            </SettingCard>
            
            <div className="bg-gray-700 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="font-bold flex items-center gap-2"><Clock size={20} /> Horas de oficina</h3>
                        <p className="text-sm text-gray-400">Define cuándo tu equipo está disponible para responder.</p>
                    </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-600 space-y-2">
                    {settings.schedule.map((item, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-800 rounded-md">
                            <select value={item.day} onChange={(e) => handleScheduleChange(index, 'day', e.target.value)} className="p-1 text-sm rounded-md bg-gray-600 border-transparent">
                                <option>Lunes a Viernes</option>
                                <option>Sábado</option>
                                <option>Domingo</option>
                                <option>Todos los días</option>
                            </select>
                            <span className="text-sm">de</span>
                            <select value={item.start} onChange={(e) => handleScheduleChange(index, 'start', e.target.value)} className="p-1 text-sm rounded-md bg-gray-600 border-transparent">
                                {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <span className="text-sm">a</span>
                             <select value={item.end} onChange={(e) => handleScheduleChange(index, 'end', e.target.value)} className="p-1 text-sm rounded-md bg-gray-600 border-transparent">
                                {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <button onClick={() => removeSchedule(index)} className="ml-auto text-gray-400 hover:text-red-500"><Trash size={16} /></button>
                        </div>
                    ))}
                     <button onClick={addSchedule} className="w-full text-sm p-2 bg-hoprin-primary/20 text-hoprin-primary rounded-md hover:bg-hoprin-primary/40">
                        + Agregar horario comercial
                    </button>
                </div>
            </div>
        </div>
    );
};

const AdminPanel = ({ agents, replies, services, setServices, currentAgent, departments, socket, activeTab, setActiveTab, channels, setChannels, qrCodes, createAgent, changeAgentPassword, deleteAgent }) => {
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [deleteInfo, setDeleteInfo] = useState({ type: null, id: null });
    const [isLoading, setIsLoading] = useState(false);
    // NOTA: Se eliminó el 'useState' y 'useEffect' para 'channels' de aquí.
    // Ahora el componente depende 100% de los props recibidos desde App.

    const handleDelete = async () => {
        setIsLoading(true);
        try {
            if (deleteInfo.type === 'agent') {
                const result = await deleteAgent({ uid: deleteInfo.id.uid, agentDocId: deleteInfo.id.docId });
                alert(result.data.result);
            } else if (deleteInfo.type === 'service') {
                // La eliminación de servicios ahora debe hacerse en la BD
                await deleteDoc(doc(db, 'services', deleteInfo.id));
            } else if (deleteInfo.type === 'quickReply') {
                await deleteDoc(doc(db, 'quickReplies', deleteInfo.id));
            }
        } catch (error) {
            alert(`Error al eliminar: ${error.message}`);
        }
        setIsLoading(false);
        setShowConfirmModal(false);
        setDeleteInfo({ type: null, id: null });
    };

    const confirmDelete = (type, id) => { setDeleteInfo({ type, id }); setShowConfirmModal(true); };
    
    return (
        <div className="p-4 md:p-8 text-white h-full overflow-y-auto">
            {isLoading && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-hoprin-primary"></div></div>}
            {showConfirmModal && <ConfirmationModal onConfirm={handleDelete} onCancel={() => setShowConfirmModal(false)} title={`¿Eliminar?`} message="Esta acción no se puede deshacer."/>}
            <h1 className="text-3xl font-bold mb-6">Panel de Administración</h1>
            <div className="flex border-b-2 border-gray-700 mb-6 flex-wrap">
                <button onClick={() => setActiveTab('users')} className={`px-4 py-2 text-lg font-medium ${activeTab === 'users' ? 'border-b-2 border-hoprin-primary text-hoprin-primary' : 'text-gray-500'}`}>Usuarios</button>
                <button onClick={() => setActiveTab('replies')} className={`px-4 py-2 text-lg font-medium ${activeTab === 'replies' ? 'border-b-2 border-hoprin-primary text-hoprin-primary' : 'text-gray-500'}`}>Respuestas Rápidas</button>
				<button onClick={() => setActiveTab('auto-responses')} className={`px-4 py-2 text-lg font-medium ${activeTab === 'auto-responses' ? 'border-b-2 border-hoprin-primary text-hoprin-primary' : 'text-gray-500'}`}>Respuestas Automáticas</button>
                <button onClick={() => setActiveTab('services')} className={`px-4 py-2 text-lg font-medium ${activeTab === 'services' ? 'border-b-2 border-hoprin-primary text-hoprin-primary' : 'text-gray-500'}`}>Servicios</button>
                <button onClick={() => setActiveTab('channels')} className={`px-4 py-2 text-lg font-medium ${activeTab === 'channels' ? 'border-b-2 border-hoprin-primary text-hoprin-primary' : 'text-gray-500'}`}>Canales</button>
               
            </div>
            {activeTab === 'users' && <UserManagementPanel agents={agents} onConfirmDelete={confirmDelete} currentAgent={currentAgent} createAgent={createAgent} changeAgentPassword={changeAgentPassword} />}
            {activeTab === 'replies' && <QuickReplyPanel replies={replies} onConfirmDelete={confirmDelete} />}
			{activeTab === 'auto-responses' && <BotSettingsPanel />}
            {activeTab === 'services' && <ServicesPanel services={services} setServices={setServices} onConfirmDelete={confirmDelete} />}
            {activeTab === 'channels' && <ChannelsPanel channels={channels} setChannels={setChannels} currentAgent={currentAgent} departments={departments} socket={socket} qrCodes={qrCodes}/>}
            
        </div>
    );
};



const LoginPage = ({ onLogin, error }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const handleSubmit = (e) => { e.preventDefault(); onLogin(email, password); };

    return (
        <div className="flex items-center justify-center min-h-screen w-screen bg-black text-white font-sans p-4"> {/* 2. FONDO NEGRO */}
            <div className="flex w-full max-w-4xl h-[550px] bg-gray-900 rounded-2xl shadow-2xl overflow-hidden"> {/* 3. PANEL GRIS OSCURO */}
                
                {/* Columna Izquierda: Formulario */}
                <div className="w-full md:w-1/2 p-8 flex flex-col justify-center items-center text-center">
                    <div className="mb-4">
                        <img src={hoprinPlusLogo} alt="Logo Hoprin+" className="h-28 w-28 mb-4 mx-auto" />
                    </div>
                    
                    <h2 className="text-3xl font-bold text-white mb-2">Iniciar Sesión</h2>
                    <p className="text-gray-400 mb-8">Suite de Atención al Cliente de Hoprin+</p>
                    
                    <form className="space-y-6 w-full max-w-sm" onSubmit={handleSubmit}>
                        {error && <p className="text-center text-red-400 bg-red-500/10 py-2 rounded-md text-sm">{error}</p>}
                        <div className="space-y-4">
                            <input id="email-address" name="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-4 py-3 border-transparent placeholder-gray-500 text-white bg-gray-800 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Email" />
                            <div className="relative">
                                <input id="password" name="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full px-4 py-3 border-transparent placeholder-gray-500 text-white bg-gray-800 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Contraseña" />
                                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-500 hover:text-white">
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>
                        <button type="submit" className="w-full flex justify-center py-3 px-4 text-sm font-bold rounded-md text-gray-900 bg-green-400 hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-green-500 transition-colors">
                            Acceder
                        </button>
                    </form>
                </div>

                {/* Columna Derecha: Imagen */}
                <div className="hidden md:block w-1/2 relative">
                    <img 
                        src={loginImage} // <-- 4. USAMOS LA IMAGEN LOCAL
                        alt="Equipo de atención al cliente en línea" 
                        className="w-full h-full object-cover"
                    />
                     <div className="absolute inset-0 bg-black/30"></div>
                </div>

            </div>
        </div>
    );
};

const AppHeader = ({ agent, onLogout, setView, accessibleNavItems, setIsCalculatorOpen, notifications, onNotificationClick, onStatusChange }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isMoreNavOpen, setIsMoreNavOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const menuRef = useRef(null);
    const moreNavRef = useRef(null);
    const notificationsRef = useRef(null);

    // NOTA: Se eliminó 'handleStatusChange' de aquí. Ahora se recibe por props.
    
    // Formateador de fecha
    const formattedDate = new Date().toLocaleDateString('es-VE', { weekday: 'long', day: 'numeric', month: 'long' });

    // Hook para cerrar menús al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) setIsMenuOpen(false);
            if (moreNavRef.current && !moreNavRef.current.contains(event.target)) setIsMoreNavOpen(false);
            if (notificationsRef.current && !notificationsRef.current.contains(event.target)) setIsNotificationsOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Variables de estado
    const isManagerOrAdmin = agent.role === 'gerente' || agent.role === 'administrador';
    const unreadCount = notifications.length;

    return (
        <header className="bg-black text-white p-4 flex justify-between items-center shadow-md z-10 shrink-0">
            
            {/* Logo y Título (Restaurado) */}
            <div className="flex items-center gap-3">
                <img src={hoprinPlusLogo} alt="Logo Hoprin+" className="h-10 w-10"/>
                <div className="hidden md:block">
                    <h1 className="font-bold text-lg">Chat Hoprin+</h1>
                    <p className="text-xs text-gray-400">{formattedDate}</p>
                </div>
            </div>

            {/* Iconos y Menús de la Derecha (Restaurados y Fusionados) */}
            <div className="flex items-center gap-4">

                {/* --- Botón de Notificaciones (Nuevo) --- */}
                <div className="relative" ref={notificationsRef}>
                    <button
                        onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                        className="relative text-gray-400 hover:text-white"
                        title="Notificaciones"
                    >
                        <Bell size={20} />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                            </span>
                        )}
                    </button>
                    {isNotificationsOpen && (
                        <div className="absolute top-full right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-gray-800 rounded-lg shadow-2xl z-50 animate-fade-in flex flex-col">
                            <div className="p-3 border-b border-gray-700 font-semibold text-sm">
                                Notificaciones ({unreadCount})
                            </div>
                            {unreadCount > 0 ? (
                                notifications.map(notif => (
                                    <button
                                        key={notif.id}
                                        onClick={() => {
                                            onNotificationClick(notif);
                                            setIsNotificationsOpen(false);
                                        }}
                                        className="w-full text-left p-3 hover:bg-gray-700 border-b border-gray-700 last:border-b-0"
                                    >
                                        <p className="text-sm">
                                            <span className="font-semibold">{notif.senderEmail?.split('@')[0] || 'Sistema'}</span> {notif.text}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-1">
                                            {formatTimestamp(notif.timestamp)} - {formatDateSeparator(notif.timestamp?.toDate())}
                                        </p>
                                    </button>
                                ))
                            ) : (
                                <p className="p-4 text-center text-gray-500 text-sm">No tienes notificaciones nuevas.</p>
                            )}
                        </div>
                    )}
                </div>
                {/* --- Fin Notificaciones --- */}

                {/* Menú de Usuario (Restaurado) */}
                <div className="relative" ref={menuRef}>
                    <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="flex items-center gap-2 text-gray-300 hover:text-white">
                        <span className="hidden md:inline">{agent.name.split(' ')[0]}</span>
                        <div className={`w-3 h-3 rounded-full ${agent.status === 'Disponible' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <ChevronDown size={16} />
                    </button>
                    {isMenuOpen && (
                        <div className="absolute top-full right-0 mt-2 w-64 bg-gray-800 rounded-lg shadow-2xl z-50 animate-fade-in">
                            <div className="p-4 border-b border-gray-700">
                                <p className="font-semibold">{agent.name}</p>
                                <p className="text-sm text-gray-400">{agent.email}</p>
                            </div>
                            <div className="p-2 space-y-1">
                                <p className="text-xs text-gray-500 px-2 font-semibold">Cambiar estado</p>
                                <button onClick={() => { onStatusChange('Disponible'); setIsMenuOpen(false); }} className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50" disabled={agent.status === 'Disponible'}>
                                    <span className="w-2 h-2 bg-green-500 rounded-full"></span> Disponible
                                </button>
                                <button onClick={() => { onStatusChange('Ausente'); setIsMenuOpen(false); }} className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50" disabled={agent.status === 'Ausente'}>
                                    <span className="w-2 h-2 bg-red-500 rounded-full"></span> Ausente
                                </button>
                            </div>
                            <div className="p-2 border-t border-gray-700">
                                <button onClick={onLogout} className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-700 flex items-center gap-2 text-red-400">
                                    <LogOut size={16} /> Cerrar Sesión
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Menú de Más Opciones (Móvil/Tablet) (Restaurado) */}
                <div className="relative md:hidden" ref={moreNavRef}>
                    <button onClick={() => setIsMoreNavOpen(!isMoreNavOpen)} className="text-gray-400 hover:text-white">
                        <MoreVertical size={20} />
                    </button>
                    {isMoreNavOpen && (
                        <div className="absolute top-full right-0 mt-2 w-56 bg-gray-800 rounded-lg shadow-2xl z-50 animate-fade-in p-2">
                            {accessibleNavItems.map(item => (
                                <button key={item.view} onClick={() => { setView(item.view); setIsMoreNavOpen(false); }} className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-700 flex items-center gap-3">
                                    <item.icon size={18} /> {item.label}
                                </button>
                            ))}
                            {isManagerOrAdmin && (
                                <button onClick={() => { setIsCalculatorOpen(true); setIsMoreNavOpen(false); }} className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-700 flex items-center gap-3">
                                    <Calculator size={18} /> Calculadora
                                </button>
                            )}
                        </div>
                    )}
                </div>

            </div>
        </header>
    );
};

const ImportContactsModal = ({ departments, onClose, onSuccess, importContacts }) => {
    const [step, setStep] = useState(1);
    const [file, setFile] = useState(null);
    const [fileContent, setFileContent] = useState("");
    const [headers, setHeaders] = useState([]);
    const [departmentId, setDepartmentId] = useState("");
    const [tagsToAdd, setTagsToAdd] = useState("");
    const [mapping, setMapping] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    // --- INICIO DE LA MODIFICACIÓN (Campo 'internalId' añadido) ---
    const firestoreFields = [
        { key: 'contactName', label: 'Nombre del Contacto' },
        { key: 'contactPhone', label: 'Teléfono del Contacto' },
        { key: 'internalId', label: 'ID del Contacto' }, // <-- AÑADIDO
        { key: 'contactNote', label: 'Nota del Contacto' },
        { key: 'tags', label: 'Etiquetas (JSON)' },
    ];

    // --- INICIO DE LA MODIFICACIÓN (Plantilla actualizada) ---
    const csvTemplate = "contactName,contactPhone,internalId,contactNote\nEjemplo Nombre,584121234567,ID-CLIENTE-123,Nota sobre el cliente";
    const templateDataUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvTemplate);
    // --- FIN DE LA MODIFICACIÓN ---

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && (selectedFile.type === "text/csv" || selectedFile.name.endsWith('.csv'))) {
            setError("");
            setFile(selectedFile);
            const reader = new FileReader();
            reader.onload = (event) => {
                let text = event.target.result;
                if (text.charCodeAt(0) === 0xFEFF) { text = text.slice(1); }
                setFileContent(text);
                const firstLine = text.split('\n')[0];
                const parsedHeaders = firstLine.split(',').map(h => h.trim().replace(/"/g, '').replace(/\r/g, ''));
                setHeaders(parsedHeaders);
            };
            reader.readAsText(selectedFile);
        } else {
            setError("Por favor, selecciona un archivo .csv");
            setFile(null);
            setHeaders([]);
        }
    };

    const handleMappingChange = (csvHeader, firestoreField) => {
        setMapping(prev => ({ ...prev, [csvHeader]: firestoreField }));
    };

    const handleNextStep = () => {
        if (step === 1 && (!file || !departmentId)) {
            setError("Debes seleccionar un archivo y un departamento.");
            return;
        }
        setError("");
        setStep(2);
    };
    
    const handleImport = async () => {
        if (!Object.values(mapping).includes('contactPhone')) {
            setError("Debes mapear obligatoriamente la columna del 'Teléfono del Contacto'.");
            return;
        }
        setIsLoading(true);
        setError("");
        try {
            
            const result = await importContacts({
                fileContent,
                departmentId,
                mapping,
                tagsToAdd: tagsToAdd.split(',').map(t => t.trim()).filter(Boolean),
            });
            
            if (result.data.success) {
                onSuccess(result.data.message);
                onClose();
            } else {
                throw new Error(result.data.message || 'Error desconocido en la importación.');
            }

        } catch (err) {
            setError(err.message);
            console.error("Error al importar:", err);
        }
        setIsLoading(false);
    };

    const renderStep1 = () => (
        <>
            <h2 className="text-2xl font-bold text-center mb-6">Importar Contactos (Paso 1 de 2)</h2>
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Asignar al Departamento</label>
                    <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className="w-full p-2 border rounded-md bg-gray-700">
                        <option value="" disabled>Selecciona una bandeja de entrada...</option>
                        {departments.map(dept => <option key={dept.id} value={dept.id}>{dept.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Subir Archivo CSV</label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-600 border-dashed rounded-md">
                        <div className="space-y-1 text-center">
                            <UploadCloud size={48} className="mx-auto text-gray-500"/>
                            <div className="flex text-sm text-gray-400">
                                <label htmlFor="file-upload" className="relative cursor-pointer bg-gray-800 rounded-md font-medium text-hoprin-primary hover:text-hoprin-accent focus-within:outline-none">
                                    <span>Sube un archivo</span>
                                    <input id="file-upload" name="file-upload" type="file" className="sr-only" accept=".csv" onChange={handleFileChange} />
                                </label>
                                <p className="pl-1">o arrástralo aquí</p>
                            </div>
                            {file && <p className="text-xs text-gray-500 mt-2">{file.name}</p>}
                        </div>
                    </div>
                    {/* --- INICIO DE LA MODIFICACIÓN (Enlace de descarga actualizado) --- */}
                    <div className="text-center mt-2">
                        <a href={templateDataUri} download="plantilla_contactos_con_id.csv" className="text-sm text-hoprin-primary hover:underline">
                            Descargar plantilla de ejemplo (.csv)
                        </a>
                    </div>
                    {/* --- FIN DE LA MODIFICACIÓN --- */}
                </div>
                 {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                <button onClick={handleNextStep} disabled={!file || !departmentId} className="w-full flex justify-center items-center gap-2 bg-hoprin-primary text-white py-2 rounded-lg mt-4 hover:opacity-90 disabled:opacity-50 transition-opacity">
                    Siguiente <ArrowRight size={18}/>
                </button>
            </div>
        </>
    );

    const renderStep2 = () => (
         <>
            <h2 className="text-2xl font-bold text-center mb-6">Mapear Columnas (Paso 2 de 2)</h2>
            <p className="text-center text-sm text-gray-400 mb-4">Asigna las columnas de tu CSV a los campos de Hoprin+.</p>
            <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                {headers.map(header => (
                    <div key={header} className="flex items-center justify-between gap-4 p-2 bg-gray-700 rounded-md">
                        <span className="font-mono text-sm">{header}</span>
                        <ArrowRight size={16} className="text-gray-500"/>
                        <select onChange={(e) => handleMappingChange(header, e.target.value)} defaultValue="" className="flex-grow p-1.5 text-sm border rounded-md bg-gray-800">
                            <option value="" disabled>Selecciona un campo...</option>
                            {/* El 'firestoreFields' ahora incluye 'ID del Contacto' */}
                            {firestoreFields.map(field => <option key={field.key} value={field.key}>{field.label}</option>)}
                        </select>
                    </div>
                ))}
            </div>
             <div className="mt-4">
                <label className="block text-sm font-medium text-gray-300 mb-1">Añadir Etiquetas Adicionales</label>
                <input type="text" value={tagsToAdd} onChange={e => setTagsToAdd(e.target.value)} placeholder="Ej: Migrado, Cliente Antiguo (separado por comas)" className="w-full p-2 border rounded-md bg-gray-700"/>
             </div>
             {error && <p className="text-red-500 text-sm text-center mt-4">{error}</p>}
             <button onClick={handleImport} disabled={isLoading} className="w-full flex justify-center items-center gap-2 bg-green-500 text-white py-2 rounded-lg mt-6 hover:bg-green-600 disabled:opacity-50 transition-opacity">
                {isLoading ? 'Importando...' : 'Confirmar e Importar'}
             </button>
        </>
    );

    return (
        <Modal onClose={onClose}>
            {step === 1 ? renderStep1() : renderStep2()}
        </Modal>
    );
};



const MainContent = ({ 
    view, agentsWithStats, chats, currentAgent, currentUser, activityLogs, selectedChat, handleSelectChat, isProfileVisible, handleToggleProfile, handleToggleChatStatus, activeFilter, setActiveFilter, departmentFilter, setDepartmentFilter, departments, services, setServices, waSocket, tgSocket, quickReplies, agents, adminActiveTab, setAdminActiveTab, channels, setChannels, onImageClick, allInternalNotes, initialChatTab, qrCodes,
    generatePerformanceReport, createAgent, changeAgentPassword, deleteAgent, importContacts // <-- Ya los recibes aquí
}) => {
	switch (view) {
        case 'dashboard': 
            return <DashboardView agentsWithStats={agentsWithStats} chats={chats} currentAgent={currentAgent} activityLogs={activityLogs} departments={departments} />;
        
        case 'chats': 
            return <ChatsView 
                allChats={chats} 
                selectedChat={selectedChat} 
                onSelectChat={handleSelectChat} 
                currentAgent={currentAgent} 
                agents={agents} 
                isProfileVisible={isProfileVisible} 
                onToggleProfile={handleToggleProfile} 
                onCloseChat={handleToggleChatStatus}
                activeFilter={activeFilter} 
                setActiveFilter={setActiveFilter} 
                departmentFilter={departmentFilter} 
                setDepartmentFilter={setDepartmentFilter} 
                departments={departments} 
                services={services} 
                waSocket={waSocket}
				tgSocket={tgSocket}
                quickReplies={quickReplies}
				allInternalNotes={allInternalNotes}
                onImageClick={onImageClick}
                initialChatTab={initialChatTab}
            />;

        case 'contacts': 
            // AHORA PASA 'importContacts'
            return <ContactsView allChats={chats} services={services} currentAgent={currentAgent} departments={departments} onImportSuccess={(message) => alert(message)} onSelectChat={handleSelectChat} isProfileVisible={isProfileVisible} onToggleProfile={handleToggleProfile} selectedChat={selectedChat} waSocket={waSocket} channels={channels} importContacts={importContacts} />;
                  
		case 'reports': 
            // AHORA PASA 'generatePerformanceReport'
            return <ReportsView departments={departments} generatePerformanceReport={generatePerformanceReport} />;
        
        case 'admin': 
            // AHORA PASA LAS 3 FUNCIONES DE AGENTE
            return <AdminPanel agents={agents} replies={quickReplies} services={services} setServices={setServices} currentAgent={currentAgent} departments={departments} socket={waSocket} activeTab={adminActiveTab} setActiveTab={setAdminActiveTab} channels={channels} setChannels={setChannels} qrCodes={qrCodes} 
                createAgent={createAgent} changeAgentPassword={changeAgentPassword} deleteAgent={deleteAgent} />;
        default: 
            return ( <div className="flex flex-grow overflow-hidden h-full"> <p className="m-auto text-white text-xl">Vista "{view}" en construcción.</p> </div> );
    }
};

const ImageModal = ({ imageUrl, onClose }) => (
    <div 
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 animate-fade-in"
        onClick={onClose} // Cierra el modal al hacer clic en el fondo
    >
        <button 
            onClick={onClose} 
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
        >
            <X size={32} />
        </button>
        <img 
            src={imageUrl} 
            alt="Vista previa de imagen" 
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()} // Evita que el clic en la imagen cierre el modal
        />
    </div>
);

export default function App() {
    const [agents, setAgents] = useState([]);
    const [quickReplies, setQuickReplies] = useState([]);
    const [chats, setChats] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [currentAgent, setCurrentAgent] = useState(null);
	const [currentUser, setCurrentUser] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);
    const [view, setView] = useState(null);
    const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [selectedChatId, setSelectedChatId] = useState(null);
    const [isProfileVisible, setIsProfileVisible] = useState(false);
    const [activeFilter, setActiveFilter] = useState('abierto');
    const [departmentFilter, setDepartmentFilter] = useState('todos');
	const [qrCodes, setQrCodes] = useState({});
    const [waSocket, setWaSocket] = useState(null);
	const [tgSocket, setTgSocket] = useState(null);
    const [adminActiveTab, setAdminActiveTab] = useState('users');
    const [services, setServices] = useState([]);
    const [activityLogs, setActivityLogs] = useState([]);
	const [channels, setChannels] = useState([]);
	const [channelStatuses, setChannelStatuses] = useState({});
	const [allInternalNotes, setAllInternalNotes] = useState({});
	const [imageModalUrl, setImageModalUrl] = useState(null);
    const [notifications, setNotifications] = useState([]);
	const [initialChatTab, setInitialChatTab] = useState('chat');
    const selectedChat = useMemo(() => chats.find(chat => chat.id === selectedChatId), [chats, selectedChatId]);
	const functions = getFunctions(auth.app, 'us-central1');
    const generatePerformanceReport = httpsCallable(functions, 'generatePerformanceReport');
    const createAgent = httpsCallable(functions, 'createAgent');
    const changeAgentPassword = httpsCallable(functions, 'changeAgentPassword');
    const deleteAgent = httpsCallable(functions, 'deleteAgent');
	const importContacts = httpsCallable(functions, 'importContacts');
    
useEffect(() => {
    // Estas son las URLs de tus servidores de Render
    const WHATSAPP_BACKEND_URL = "https://hoprinplus-whatsapp.onrender.com";
    const TELEGRAM_BACKEND_URL = "https://hoprinplus-backend.onrender.com";

    console.log(`Conectando a WA: ${WHATSAPP_BACKEND_URL}`);
    console.log(`Conectando a TG: ${TELEGRAM_BACKEND_URL}`);

    const socketOptions = {
    transports: ['websocket'], // Forzar websocket evita problemas de polling en Render
    upgrade: false,
    secure: true,
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 3000
};

    // --- Conexión al Servidor WhatsApp ---
    const newWaSocket = io(WHATSAPP_BACKEND_URL, socketOptions);
    setWaSocket(newWaSocket);

    // --- Conexión al Servidor Telegram ---
    const newTgSocket = io(TELEGRAM_BACKEND_URL, socketOptions);
    setTgSocket(newTgSocket);

    // --- Lógica de Eventos ---
    requestNotificationPermission();

    const handleEnvioFallido = ({ chatId, error }) => {
        console.error(`Error de envío en chat ${chatId}:`, error);
        alert(`No se pudo enviar el mensaje. Razón: ${error}`);
    };
    newWaSocket.on('envio_fallido', handleEnvioFallido);
    newTgSocket.on('envio_fallido', handleEnvioFallido);

    newWaSocket.on('channel_status_update', ({ channelId, status, message }) => {
        // Guardamos el estado en la variable separada, no tocamos la lista principal
        setChannelStatuses(prev => ({
            ...prev,
            [channelId]: { connected: status, statusMessage: message }
        }));
    });
	
    newWaSocket.on('qr_update', ({ channelId, qrCodeUrl }) => {
        setQrCodes(prev => ({ ...prev, [channelId]: qrCodeUrl }));
    });
    
    // Limpieza
    return () => {
        newWaSocket.close();
        newTgSocket.close();
    }
}, []); // Solo se ejecuta una vez

    useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
        if (user) {
            try {
                const agentsQuery = query(
                    collection(db, 'agents'),
                    where("uid", "==", user.uid),
                    limit(1)
                );
                const querySnapshot = await getDocs(agentsQuery);

                if (!querySnapshot.empty) {
                    const agentProfileDoc = querySnapshot.docs[0];
                    const agentProfile = {
                        id: agentProfileDoc.id,
                        ...agentProfileDoc.data()
                    };

                    setCurrentAgent(agentProfile);
                    setCurrentUser(user); // ✅ Nuevo: guarda el objeto Firebase Auth
                    setLoginError('');

                    const isOperator = agentProfile.role === 'agente' || agentProfile.role === 'supervisor';
                    setView(isOperator ? 'chats' : 'dashboard');
                } else {
                    console.error("Perfil de agente no encontrado en Firestore para UID:", user.uid);
                    setLoginError("Acceso concedido, pero el perfil del agente no existe en la base de datos.");
                    setCurrentUser(null);
                    await signOut(auth);
                }
            } catch (error) {
                console.error("Error al obtener el perfil del agente:", error);
                setCurrentUser(null);
                await signOut(auth);
            }
        } else {
            setCurrentAgent(null);
            setCurrentUser(null); // ✅ Nuevo: limpia el usuario si no hay sesión
            setView('login');
        }

        setAuthChecked(true); // ✅ Solo se marca como verificado al final
    });

    return () => unsubscribeAuth();
}, []);


useEffect(() => {
    // Este useEffect ahora NO depende de los sockets, solo de Firestore
    if (!currentAgent?.uid) return;

    const unsubAgents = onSnapshot(query(collection(db, 'agents')), s => setAgents(s.docs.map(d => ({id: d.id, ...d.data()}))));
    const unsubReplies = onSnapshot(query(collection(db, 'quickReplies'), orderBy('createdAt', 'desc')), s => setQuickReplies(s.docs.map(d => ({id: d.id, ...d.data()}))));
    const unsubChats = onSnapshot(query(collection(db, 'chats')), (snapshot) => setChats(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
    const activityQuery = query(collection(db, 'activityLogs'), orderBy('timestamp', 'desc'), limit(10));
    const unsubActivity = onSnapshot(activityQuery, (snapshot) => setActivityLogs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
    const unsubChannels = onSnapshot(query(collection(db, 'channels')), (snapshot) => setChannels(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
    const unsubDepartments = onSnapshot(query(collection(db, 'departments')), (snapshot) => setDepartments(snapshot.docs.map(d => ({id: d.id, ...d.data()}))));
    const unsubServices = onSnapshot(query(collection(db, 'services')), (snapshot) => setServices(snapshot.docs.map(d => ({id: d.id, ...d.data()}))));
    
    const notesQuery = query(collectionGroup(db, 'internal_notes'));
    const unsubNotes = onSnapshot(notesQuery, (snapshot) => {
        const allNotes = {};
        snapshot.forEach(doc => {
            const pathParts = doc.ref.path.split('/');
            const chatId = pathParts[1];
            if (!allNotes[chatId]) allNotes[chatId] = [];
            allNotes[chatId].push({ id: doc.id, ...doc.data() });
        });
        setAllInternalNotes(allNotes);
    }, (error) => console.error("Error al escuchar todas las notas internas:", error));
    
    // Limpieza
    return () => {
        unsubAgents(); unsubReplies(); unsubDepartments(); unsubChats();
        unsubActivity(); unsubChannels(); unsubServices();
        unsubNotes();
    };
}, [currentAgent]); // <-- Dependencia de socket eliminada


// --- NUEVO useEffect ---
// Pega este NUEVO useEffect justo después del que acabas de modificar
useEffect(() => {
    // Este efecto maneja la solicitud de estado del canal de WA
    if (waSocket) {
        // Pedimos los estados actuales en cuanto el socket de WA se conecta
        waSocket.emit('get_all_channel_statuses');
    }
}, [waSocket]); // Se ejecuta solo cuando waSocket está listo

useEffect(() => {
    // Asegúrate de que AMBOS sockets y el agente existan
    if (!waSocket || !tgSocket || !currentAgent) return;

    // Función que manejará la notificación (sin cambios)
    const handleNewAssignment = ({ chatId, agentEmail }) => {
        if (agentEmail === currentAgent.email) {
            const assignedChat = chats.find(c => c.id === chatId);
            if (assignedChat) {
                console.log(`Mostrando notificación para nuevo chat asignado: ${assignedChat.contactName}`);
                showNotification( `Nuevo chat: ${assignedChat.contactName}`, { body: assignedChat.lastMessage || 'Nuevo chat asignado', icon: hoprinPlusLogo, tag: chatId });
            } else {
                 console.warn(`Se recibió asignación para chat ${chatId}, pero no se encontró.`);
                 showNotification( `Nuevo chat asignado`, { body: 'Tienes una nueva conversación asignada.', icon: hoprinPlusLogo, tag: chatId });
            }
        }
    };

    // Escuchar el evento en AMBOS servidores
    waSocket.on('new_chat_assigned', handleNewAssignment);
    tgSocket.on('new_chat_assigned', handleNewAssignment);

    // Limpieza
    return () => {
        waSocket.off('new_chat_assigned', handleNewAssignment);
        tgSocket.off('new_chat_assigned', handleNewAssignment);
    };
}, [waSocket, tgSocket, currentAgent, chats]); // <-- Depende de ambos sockets

useEffect(() => {
    // Solo escuchar si tenemos un agente actual
    if (!currentAgent?.email) {
        setNotifications([]); // Limpiar notificaciones si no hay agente
        return;
    }

    console.log(`[Notificaciones] Escuchando para ${currentAgent.email}`);

    // Query para obtener notificaciones NO LEÍDAS para el agente actual
    const notificationsQuery = query(
        collection(db, 'notifications'),
        where('recipientEmail', '==', currentAgent.email),
        where('read', '==', false),
        orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
        const fetchedNotifications = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        setNotifications(fetchedNotifications);
        console.log(`[Notificaciones] ${fetchedNotifications.length} notificaciones no leídas encontradas.`);
    }, (error) => {
        console.error("[Notificaciones] Error al escuchar notificaciones:", error);
        setNotifications([]);
    });

    // Limpieza del listener
    return () => {
        console.log(`[Notificaciones] Dejando de escuchar para ${currentAgent?.email}`);
        unsubscribe();
    };
}, [currentAgent?.email]); // <-- FIN DEL TERCER useEffect


useEffect(() => {
    if (!currentAgent?.id || currentAgent.status === 'Ausente') return;

    const updateActivity = async () => {
        const now = Date.now();
        const lastUpdate = localStorage.getItem('lastActivityUpdate');
        const FIVE_MINUTES = 5 * 60 * 1000;

        // Solo actualizamos si han pasado más de 5 minutos desde la última vez
        // para no saturar la base de datos con escrituras en cada clic.
        if (!lastUpdate || now - parseInt(lastUpdate) > FIVE_MINUTES) {
            try {
                await updateDoc(doc(db, 'agents', currentAgent.id), {
                    lastActive: serverTimestamp() // Marca la hora actual en el servidor
                });
                localStorage.setItem('lastActivityUpdate', now.toString());
                console.log("🕒 Actividad del agente actualizada.");
            } catch (error) {
                console.error("Error actualizando actividad:", error);
            }
        }
    };

    // Escuchar eventos de usuario (movimiento, teclas, clics)
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    const handler = () => updateActivity();
    
    events.forEach(e => window.addEventListener(e, handler));
    
    // Actualizar inmediatamente al cargar (login/refresh)
    updateActivity();
    
    return () => events.forEach(e => window.removeEventListener(e, handler));
}, [currentAgent?.id, currentAgent?.status]);



    const agentsWithStats = useMemo(() => {
        return agents.map(agent => {
            const openChats = chats.filter(chat => chat.agentEmail === agent.email && chat.status === 'Abierto').length;
            return { ...agent, openChats };
        });
    }, [chats, agents]);
    
    const handleLogin = async (email, password) => { setLoginError(''); try { await signInWithEmailAndPassword(auth, email, password); } catch (error) { setLoginError('Email o contraseña incorrectos.'); } };
    const handleLogout = async () => {
        if (currentAgent && currentAgent.id) {
            const agentRef = doc(db, 'agents', currentAgent.id);
            try { await updateDoc(agentRef, { status: 'Ausente' }); } catch (error) { console.error("Error al actualizar el estado al cerrar sesión:", error); }
        }
        signOut(auth);
    };
	
	const handleAgentStatusChange = async (newStatus) => {
        if (currentAgent && currentAgent.id) {
            
            // 1. Actualizar el estado local INMEDIATAMENTE
            // Esto hace que la UI reaccione al instante.
            setCurrentAgent(prevAgent => ({
                ...prevAgent,
                status: newStatus 
            }));
            
            // 2. Actualizar Firestore en segundo plano
            const agentRef = doc(db, 'agents', currentAgent.id);
            try {
                await updateDoc(agentRef, { status: newStatus });
            } catch (error) {
                console.error("Error al actualizar el estado del agente en Firestore:", error);
                // Si falla, revertimos el estado local al estado original
                setCurrentAgent(prevAgent => ({
                    ...prevAgent,
                    status: currentAgent.status // Revertir al estado original
                }));
            }
        }
    };
	
    const handleSelectChat = useCallback((chatId, switchToChatsView = false) => {
        setSelectedChatId(chatId);
        setInitialChatTab('chat'); // <-- AÑADIDO: Resetea a la pestaña 'chat' por defecto
        if(!isProfileVisible && window.innerWidth >= 768) {
            setIsProfileVisible(true);
        }
        if (switchToChatsView) {
            setView('chats');
        }
    }, [isProfileVisible]);
	
    const handleToggleProfile = useCallback(() => { setIsProfileVisible(prev => !prev); }, []);

    const handleToggleChatStatus = useCallback(async (chatToToggle) => {
    if (!chatToToggle) return;
    const chatRef = doc(db, 'chats', chatToToggle.id);

    try {
        if (chatToToggle.status === 'Abierto') {
            // Lógica para CERRAR un chat
            await updateDoc(chatRef, { 
                status: 'Cerrado', 
                ratingPending: true, 
                closedAt: serverTimestamp() 
            });
            await addDoc(collection(db, 'activityLogs'), { 
                agentName: currentAgent.name, 
                action: 'cerró el chat con', 
                target: chatToToggle.contactName, 
                timestamp: serverTimestamp() 
            });
            if (chatToToggle.platform === 'whatsapp' && waSocket) {
    waSocket.emit('solicitar_calificacion', { chatId: chatToToggle.id });
} else if (chatToToggle.platform === 'telegram' && tgSocket) {
    tgSocket.emit('solicitar_calificacion', { chatId: chatToToggle.id });
}
            setSelectedChatId(null); // Cerramos la ventana del chat
        } else {
            // Lógica para REABRIR un chat
            await updateDoc(chatRef, { 
                status: 'Abierto', 
                ratingPending: false,
                closedAt: null // Limpiamos la fecha de cierre
            });
            await addDoc(collection(db, 'activityLogs'), { 
                agentName: currentAgent.name, 
                action: 'reabrió el chat con', 
                target: chatToToggle.contactName, 
                timestamp: serverTimestamp() 
            });
            // No cerramos la ventana, el chat ahora está activo de nuevo.
        }
    } catch (error) {
        console.error("Error al cambiar el estado del chat:", error);
    }
}, [waSocket, tgSocket, currentAgent]);

	const handleNotificationClick = useCallback(async (notification) => {
        if (!notification || !notification.chatId) return;

        console.log(`[Notificación Click] Navegando a chat ${notification.chatId}, nota ${notification.noteId}`);

        // 1. Navegar a la vista de chats y seleccionar el chat
        setView('chats');
        setSelectedChatId(notification.chatId);
        setInitialChatTab('notes'); // <-- AÑADIDO: Establece la pestaña inicial a 'notes'

        // 2. Marcar la notificación como leída en Firestore
        try {
            const notifRef = doc(db, 'notifications', notification.id);
            await updateDoc(notifRef, { read: true });
            console.log(`[Notificación Click] Notificación ${notification.id} marcada como leída.`);
        } catch (error) {
            console.error(`[Notificación Click] Error al marcar notificación ${notification.id} como leída:`, error);
        }

    }, []);

    const navItems = useMemo(() => [ 
        { view: 'dashboard', label: 'Dashboard', icon: LayoutDashboard }, { view: 'chats', label: 'Chats', icon: MessageSquarePlus },
        { view: 'contacts', label: 'Contactos', icon: UsersIcon },
        { view: 'reports', label: 'Reportes', icon: TrendingUp },
        { view: 'admin', label: 'Admin', icon: Settings },
    ], []);
    
    const accessibleNavItems = useMemo(() => {
        if (!currentAgent) return [];
        const { role } = currentAgent;
        return navItems.filter(item => {
            switch (item.view) {
                // Supervisores, Gerentes y Admins pueden ver Dashboard y Reportes
                case 'dashboard':
                case 'reports':
                    return role === 'supervisor' || role === 'gerente' || role === 'administrador';
                
                // Gerentes y Admins pueden ver Contactos
                case 'contacts':
                    return role === 'gerente' || role === 'administrador';

                // Solo Admins pueden ver el resto de secciones especiales
                case 'admin':
                
                    return role === 'administrador';
                
                default: // Chats es visible para todos
                    return true;
            }
        });
    }, [currentAgent, navItems]);
	
	const mergedChannels = useMemo(() => {
        return channels.map(channel => ({
            ...channel,
            // Si tenemos un estado en vivo, lo usamos. Si no, asumimos desconectado.
            connected: channelStatuses[channel.id]?.connected || 'DISCONNECTED',
            statusMessage: channelStatuses[channel.id]?.statusMessage || ''
        }));
    }, [channels, channelStatuses]);
    
    if (!authChecked) { return <div className="h-screen w-screen bg-black flex items-center justify-center"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-hoprin-primary"></div></div>; }
    if (!currentAgent) { return <LoginPage onLogin={handleLogin} error={loginError} />; }

    const BottomNavBar = ({ setView, currentAgent, view, accessibleNavItems, setIsCalculatorOpen, selectedChatId }) => {
        const isManagerOrAdmin = currentAgent.role === 'gerente' || currentAgent.role === 'administrador';

        // Si es agente o supervisor, la barra no se muestra.
        if (!isManagerOrAdmin) {
            return null;
        }

        // Para Gerentes/Admins, oculta la barra si están DENTRO de un chat.
        if (view === 'chats' && selectedChatId) {
            return null;
        }

        // Construimos los botones en el orden deseado
        const dashboardButton = accessibleNavItems.find(item => item.view === 'dashboard');
        const chatsButton = accessibleNavItems.find(item => item.view === 'chats');
        const contactsButton = accessibleNavItems.find(item => item.view === 'contacts');
        const adminButton = accessibleNavItems.find(item => item.view === 'admin');

        const renderNavButton = (item) => {
            if (!item) return null;
            return (
                <button 
                    key={item.view} 
                    onClick={() => setView(item.view)} 
                    className={`flex flex-col items-center p-1 rounded-lg w-20 ${view === item.view ? 'text-hoprin-accent' : ''}`}
                >
                    <item.icon size={20}/>
                    <span className="text-xs mt-1">{item.label === 'Admin' ? 'Ajustes' : item.label}</span>
                </button>
            );
        };

        return (
            <nav className="md:hidden fixed bottom-0 left-0 w-full bg-black p-2 flex justify-around items-center text-white border-t border-gray-700 z-40">
                {renderNavButton(dashboardButton)}
                {renderNavButton(chatsButton)}
                {renderNavButton(contactsButton)}
                
                <button 
                    onClick={() => setIsCalculatorOpen(true)} 
                    className="flex flex-col items-center p-1 rounded-lg w-20" 
                    title="Calculadora"
                >
                    <Calculator size={20}/>
                    <span className="text-xs mt-1">Calcular</span>
                </button>

                {renderNavButton(adminButton)}
            </nav>
        );
    };
    
    return (
        <div className="h-screen w-screen bg-gray-900 flex flex-col md:flex-row font-sans dark">
            {isCalculatorOpen && <CalculatorModal onClose={() => setIsCalculatorOpen(false)} />}
			{imageModalUrl && <ImageModal imageUrl={imageModalUrl} onClose={() => setImageModalUrl(null)} />}
            <nav className="hidden md:flex w-20 bg-black p-2 flex-col items-center justify-between text-white pt-6 shrink-0">
                <div className="flex flex-col items-center gap-4">
                  {accessibleNavItems.map(item => (<button key={item.view} onClick={() => setView(item.view)} className={`p-3 rounded-lg ${view === item.view ? 'bg-hoprin-primary' : 'hover:bg-gray-700'} transition-colors`} title={item.label}><item.icon /></button>))}
                </div>
                <div className="pb-4"><button onClick={() => setIsCalculatorOpen(true)} className="p-3 rounded-lg hover:bg-gray-700 transition-colors" title="Calculadora"><Calculator /></button></div>
            </nav>
            <div className="flex-1 flex flex-col overflow-hidden">
                <AppHeader 
                    agent={currentAgent} 
                    onLogout={handleLogout} 
                    setView={setView} 
                    accessibleNavItems={accessibleNavItems}
                    setIsCalculatorOpen={setIsCalculatorOpen}
					notifications={notifications}
					onNotificationClick={handleNotificationClick}
					onStatusChange={handleAgentStatusChange}
                />
                <div className={`flex-grow overflow-y-auto md:pb-0 ${(currentAgent.role === 'gerente' || currentAgent.role === 'administrador') && !(view === 'chats' && selectedChatId) ? 'pb-16' : ''}`}>
                    <MainContent 
                        view={view}
                        agentsWithStats={agentsWithStats}
                        chats={chats}
                        currentAgent={currentAgent}
						currentUser={currentUser}
                        activityLogs={activityLogs}
                        selectedChat={selectedChat}
                        handleSelectChat={handleSelectChat}
                        isProfileVisible={isProfileVisible}
                        handleToggleProfile={handleToggleProfile}
                        handleToggleChatStatus={handleToggleChatStatus}
                        activeFilter={activeFilter}
                        setActiveFilter={setActiveFilter}
                        departmentFilter={departmentFilter}
                        setDepartmentFilter={setDepartmentFilter}
                        departments={departments}
                        services={services}
                        setServices={setServices}
                        waSocket={waSocket}
                        tgSocket={tgSocket}
                        quickReplies={quickReplies}
                        agents={agents}
                        adminActiveTab={adminActiveTab}
                        setAdminActiveTab={setAdminActiveTab}
                        channels={mergedChannels}
                        setChannels={setChannels}
						onImageClick={setImageModalUrl}
						allInternalNotes={allInternalNotes}
						qrCodes={qrCodes}
						generatePerformanceReport={generatePerformanceReport}
                        createAgent={createAgent}
                        changeAgentPassword={changeAgentPassword}
                        deleteAgent={deleteAgent}
                        importContacts={importContacts}
                    />
                </div>
                {/* <-- INICIO DE LA CORRECCIÓN: Se añaden los props que faltaban --> */}
                <BottomNavBar 
                    setView={setView} 
                    currentAgent={currentAgent} 
                    view={view} 
                    accessibleNavItems={accessibleNavItems}
                    setIsCalculatorOpen={setIsCalculatorOpen}
                    selectedChatId={selectedChatId}
                />
                {/* <-- FIN DE LA CORRECCIÓN --> */}
            </div>
        </div>
    );
}
